import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EventInput } from '@fullcalendar/core';
import { KeycloakService } from 'keycloak-angular';
import { saveAs } from 'file-saver';
import { firstValueFrom, forkJoin, of, Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { NzImageService } from 'ng-zorro-antd/image';
import { mapBackendAppointmentDataToCalendarEvents } from 'src/app/components/ehr/appointments/event-utils';
import { DEPARTMENT_LIST } from 'src/app/components/ehr/appointments/event-utils2';
import { validateUpload } from 'src/app/guards/file.guard';
import { IAppointment } from 'src/app/interfaces/Appointment/IAppointment';
import {
  IAppointmentDropdownData,
  IAppointmentStatus,
} from 'src/app/interfaces/Appointment/IAppointmentDropdownData';
import { ICarePlanAttachmentDetailed } from 'src/app/interfaces/CarePlan/ICarePlanAttachment';
import {
  ICarePlanActivitiesDetailed,
  ICarePlanAppointmentActivity,
  ICarePlanDetailed,
  ICarePlanDropdown,
  ICarePlanMedicationActivity,
  ICarePlanServiceRequestActivity,
  ICarePlanRequest,
} from 'src/app/interfaces/CarePlan/ICarePlan';
import { IMedicationDetailed } from 'src/app/interfaces/Medication/IMedicationDetailed';
import { IServiceRequestDetailed } from 'src/app/interfaces/ServiceRequest/IServiceRequest';
import { AppointmentService } from 'src/app/services/appointment.service';
import { CalendarEventsService } from 'src/app/services/calendarEvents.service';
import { CarePlanService } from 'src/app/services/carePlan.service';
import { CurrentMedicationService } from 'src/app/services/currentMedication.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PastMedicationService } from 'src/app/services/pastMedication.service';
import { PatientService } from 'src/app/services/patient.service';
import { ServiceRequestService } from 'src/app/services/serviceRequest.service';
import { SwalToastService } from 'src/app/services/swal.service';

interface LinkedAppointmentRow {
  uiId: string;
  linkId?: number;
  appointmentId: number;
  appointmentStatusId?: number | null;
  appointmentServiceTypeId?: number | null;
  appointmentReasonId?: number | null;
  activityState: string;
  status: string;
  serviceType: string;
  reason: string;
  description: string;
  nextSlotStart: string;
  lastSlotEnd: string;
  nextSlotStartRaw?: string | null;
  lastSlotEndRaw?: string | null;
  deleteOnCancel: boolean;
  appointmentEvent?: EventInput | null;
}

interface AvailableAppointmentRow {
  appointmentId: number;
  appointmentStatusId?: number | null;
  appointmentServiceTypeId?: number | null;
  appointmentReasonId?: number | null;
  when: string;
  status: string;
  serviceType: string;
  reason: string;
  description: string;
  nextSlotStartRaw?: string | null;
  lastSlotEndRaw?: string | null;
}

interface LinkedMedicationRow {
  uiId: string;
  linkId?: number;
  medicationId: number;
  sourceMedicationId: number;
  activityState: string;
  actionTypeRaw: 'continue' | 'stop';
  medicationActionType: string;
  medication: string;
  timing: string;
  assignedDoctor: string;
  onSetDateTime?: string | null;
  endDateTime?: string | null;
  deleteOnCancel: boolean;
}

interface AvailableMedicationRow {
  medicationId: number;
  medication: string;
  timing: string;
  assignedDoctor: string;
  onSetDateTime?: string | null;
  endDateTime?: string | null;
  replacementId?: number;
}

interface LinkedServiceRequestRow {
  uiId: string;
  linkId?: number;
  serviceRequestId: number;
  activityState: string;
  status: string;
  intent: string;
  priority: string;
  category: string;
  procedure: string;
  occurrence: string;
  description: string;
  deleteOnCancel: boolean;
}

interface AvailableServiceRequestRow {
  serviceRequestId: number;
  activityState: string;
  status: string;
  intent: string;
  priority: string;
  category: string;
  procedure: string;
  occurrence: string;
  description: string;
}

interface AttachmentDownloadPayload {
  blob: Blob;
  fileName: string;
}

interface CarePlanAttachmentRow {
  uiId: string;
  id?: number;
  carePlanId?: number;
  date?: string | null;
  description?: string | null;
  fileName: string;
  contentType?: string | null;
  size?: number | null;
  createdDate?: string | null;
  createdByUserId?: string | null;
  isSubmitted: boolean;
  downloadUrl?: string | null;
  file?: File;
  isPendingUpload: boolean;
  objectUrl?: string | null;
}

interface SaveRollbackContext {
  createdCarePlanId?: number;
  summaryUpdated: boolean;
  deletedAppointmentRows: LinkedAppointmentRow[];
  deletedMedicationRows: LinkedMedicationRow[];
  deletedServiceRequestRows: LinkedServiceRequestRow[];
  addedAppointmentIds: number[];
  addedMedicationRows: LinkedMedicationRow[];
  addedServiceRequestIds: number[];
  uploadedAttachmentIds: number[];
}

function periodValidator(control: AbstractControl): ValidationErrors | null {
  const period = control.get('period')?.value as Date[] | null;
  const periodStart = period?.[0];
  const periodEnd = period?.[1];

  if (!periodStart || !periodEnd) {
    return null;
  }

  return new Date(periodEnd).getTime() < new Date(periodStart).getTime()
    ? { invalidPeriod: true }
    : null;
}

@Component({
  selector: 'app-care-plan-add-edit',
  templateUrl: './care-plan-add-edit.component.html',
  styleUrls: ['./care-plan-add-edit.component.css'],
  standalone: false,
})
export class CarePlanAddEditComponent {
  private destroy$ = new Subject<void>();
  private uiSequence = 0;
  private suppressDirtyTracking = false;

  @ViewChild('attachmentFileInput', { static: false })
  attachmentFileInput?: ElementRef<HTMLInputElement>;

  isLoading = false;
  isDropdownLoading = false;
  isActivitiesLoading = false;
  isAvailableAppointmentsLoading = false;
  isAvailableMedicationsLoading = false;
  isAvailableServiceRequestsLoading = false;
  isAttachmentsLoading = false;
  isAttachmentUploadVisible = false;
  isAttachmentUploading = false;
  attachmentPreviewUrl?: string;
  private attachmentPreviewOwnsUrl = false;
  isPdfPreviewVisible = false;
  pdfPreviewFileName?: string;
  isEdit = false;
  isReadOnly = false;
  editCarePlanId?: number;
  carePlanSubmitted = false;

  dropdownData?: ICarePlanDropdown;
  appointmentDropdownData?: IAppointmentDropdownData;
  appointmentStatuses: IAppointmentStatus[] = [];

  isAppointmentLinkVisible = false;
  isMedicationLinkVisible = false;
  isMedicationAddVisible = false;
  isServiceRequestLinkVisible = false;
  isServiceRequestAddVisible = false;

  linkedAppointmentRows: LinkedAppointmentRow[] = [];
  availableAppointmentRows: AvailableAppointmentRow[] = [];
  linkedMedicationRows: LinkedMedicationRow[] = [];
  availableMedicationRows: AvailableMedicationRow[] = [];
  linkedServiceRequestRows: LinkedServiceRequestRow[] = [];
  availableServiceRequestRows: AvailableServiceRequestRow[] = [];
  attachmentRows: CarePlanAttachmentRow[] = [];
  attachmentFile?: File;

  private pendingMedicationLinkAfterCreate = false;
  private loadedCarePlanSnapshot?: ICarePlanDetailed;
  private baselineRequestSnapshot: ICarePlanRequest = {};
  private baselineAppointmentRows: LinkedAppointmentRow[] = [];
  private baselineMedicationRows: LinkedMedicationRow[] = [];
  private baselineServiceRequestRows: LinkedServiceRequestRow[] = [];
  private baselineAttachmentRows: CarePlanAttachmentRow[] = [];
  private createdDraftAppointmentIds = new Set<number>();
  private createdDraftCarePlanId?: number;
  private isSubmittedRevisionDraft = false;
  private createdDraftCurrentMedicationIds = new Set<number>();
  private createdDraftStopMedicationIds = new Set<number>();
  private createdDraftServiceRequestIds = new Set<number>();

  carePlanForm: FormGroup = new FormGroup({});
  attachmentForm: FormGroup = new FormGroup({});

  constructor(
    private readonly carePlanService: CarePlanService,
    private readonly calendarEventsService: CalendarEventsService,
    private readonly currentMedicationService: CurrentMedicationService,
    private readonly pastMedicationService: PastMedicationService,
    private readonly appointmentService: AppointmentService,
    private readonly patientService: PatientService,
    private readonly eocService: EpisodeOfCareService,
    private readonly serviceRequestService: ServiceRequestService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly keycloakService: KeycloakService,
    private readonly imageService: NzImageService,
  ) {}

  ngOnInit() {
    this.carePlanForm = this.formBuilder.group(
      {
        statusId: [null, Validators.required],
        intentId: [null, Validators.required],
        categoryId: [null],
        title: [null],
        description: [null],
        notes: [null],
        period: [null],
      },
      { validators: periodValidator },
    );
    this.attachmentForm = this.formBuilder.group({
      date: [null],
      description: [null],
      file: [null, Validators.required],
    });

    this.carePlanForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDirtyState());

    this.carePlanService.carePlanReadOnlySub
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly) => {
        this.isReadOnly = readOnly;
        this.syncReadOnlyState();
        this.updateDirtyState();
      });

    this.carePlanService.carePlanEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.populateForm(data));

    this.currentMedicationService.medicationOpenSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isMedicationAddVisible = isOpen;
      });

    this.currentMedicationService.medicationCreatedSub
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (medicationId) => {
        if (!medicationId || !this.pendingMedicationLinkAfterCreate) {
          return;
        }

        this.pendingMedicationLinkAfterCreate = false;
        this.currentMedicationService.medicationCreatedSub.next(null);
        await this.stageCreatedMedication(medicationId);
      });

    this.appointmentService.appointmentStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => {
        this.appointmentStatuses = statuses ?? [];
        this.refreshAppointmentLabels();
      });

    this.loadDropdownData();
    this.loadAppointmentLookups();
  }

  ngOnDestroy() {
    this.currentMedicationService.medicationCreatedSub.next(null);
    this.carePlanService.carePlanDirtySub.next(false);

    if (this.hasDraftResourcesToCleanup() || this.createdDraftCarePlanId) {
      void this.discardUnsavedDraftState(false);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasInvalidPeriod(): boolean {
    return (
      this.carePlanSubmitted && this.carePlanForm.hasError('invalidPeriod')
    );
  }

  get hasPastStartDateError(): boolean {
    return (
      this.carePlanSubmitted &&
      this.isPastStartDateBlocked(this.getSelectedPeriodStart())
    );
  }

  get editorTitle(): string {
    if (this.isReadOnly) {
      return 'View Care Plan';
    }

    return this.isEdit ? 'Edit Care Plan' : 'Add Care Plan';
  }

  get cancelLabel(): string {
    return this.isReadOnly ? 'Close' : 'Cancel';
  }

  get canModifyActivities(): boolean {
    return !this.isReadOnly;
  }

  get showViewActions(): boolean {
    return this.canModifyActivities || this.isReadOnly;
  }

  get medicationModalTitle(): string {
    return this.currentMedicationService.medicationModalData.isReadOnly
      ? 'Medication'
      : 'Create planned medication';
  }

  get canViewAttachments(): boolean {
    return !!this.editCarePlanId || !this.isReadOnly;
  }

  get canManageAttachments(): boolean {
    if (this.isReadOnly) {
      return false;
    }

    if (this.isSubmittedRevisionDraft) {
      return false;
    }

    if (!this.editCarePlanId || this.createdDraftCarePlanId === this.editCarePlanId) {
      return true;
    }

    return (
      !!this.editCarePlanId &&
      !this.loadedCarePlanSnapshot?.isSubmitted &&
      this.isCurrentUserCarePlanAuthor(this.loadedCarePlanSnapshot)
    );
  }

  get patientContextLabel(): string {
    const patient = this.patientService.patientInfoSub.value;
    const patientId = this.patientService.patientIDsignal();
    const fullName =
      `${patient.firstName || ''} ${patient.lastName || ''}`.trim();

    if (fullName && patientId) {
      return `${fullName} - Patient ${patientId}`;
    }

    return fullName || (patientId ? `Patient ${patientId}` : '-');
  }

  get episodeContextLabel(): string {
    return this.eocService.currentEocId()
      ? `EOC-${this.eocService.currentEocId()}`
      : '-';
  }

  get visitContextLabel(): string {
    return this.eocService.currentVisitId()
      ? `VIS-${this.eocService.currentVisitId()}`
      : '-';
  }

  async handleCancel() {
    if (this.isReadOnly) {
      this.closeEditor();
      return;
    }

    const discarded = await this.discardUnsavedDraftState(true);
    if (!discarded) {
      return;
    }

    this.restoreLocalSnapshot();
    this.closeEditor();
  }

  async submitForm(event: Event) {
    event.stopPropagation();
    event.preventDefault(); // Prevent the form from being submitted
    if (this.isReadOnly) {
      return;
    }

    this.carePlanSubmitted = true;
    this.carePlanForm.markAllAsTouched();

    if (
      this.carePlanForm.invalid ||
      this.isPastStartDateBlocked(this.getSelectedPeriodStart())
    ) {
      return;
    }

    const activityBoundaryError = this.getActivityBoundaryValidationMessage();
    if (activityBoundaryError) {
      this.swalToastService.toastError(activityBoundaryError);
      return;
    }

    const request = this.buildCarePlanRequest();
    const summaryPatch = this.buildCarePlanUpdatePatch(request);
    const hasSummaryChanges = Object.keys(summaryPatch).length > 0;
    const rollbackContext: SaveRollbackContext = {
      summaryUpdated: false,
      deletedAppointmentRows: [],
      deletedMedicationRows: [],
      deletedServiceRequestRows: [],
      addedAppointmentIds: [],
      addedMedicationRows: [],
      addedServiceRequestIds: [],
      uploadedAttachmentIds: [],
    };

    this.isLoading = true;

    try {
      if (this.isEdit && this.editCarePlanId) {
        const isSubmittedEdit = !!this.loadedCarePlanSnapshot?.isSubmitted;
        const hasActivityChanges =
          this.getPendingAppointmentAdditions().length > 0 ||
          this.getPendingAppointmentDeletions().length > 0 ||
          this.getPendingMedicationAdditions().length > 0 ||
          this.getPendingMedicationDeletions().length > 0 ||
          this.getPendingServiceRequestAdditions().length > 0 ||
          this.getPendingServiceRequestDeletions().length > 0;

        if (isSubmittedEdit && !hasSummaryChanges && !hasActivityChanges) {
          this.carePlanService.carePlanDirtySub.next(false);
          this.closeEditor();
          return;
        }

        if (hasSummaryChanges || isSubmittedEdit) {
          const editResponse = await firstValueFrom(
            this.carePlanService.editCarePlan({
              id: this.editCarePlanId,
              ...summaryPatch,
            }),
          );

          const savedCarePlanId = editResponse.data ?? this.editCarePlanId;
          if (isSubmittedEdit && savedCarePlanId !== this.editCarePlanId) {
            rollbackContext.createdCarePlanId = savedCarePlanId;
            await this.activateSubmittedCarePlanRevision(savedCarePlanId, request);
          } else {
            rollbackContext.summaryUpdated = hasSummaryChanges;
          }
        }
        await this.applyActivityChanges(this.editCarePlanId, rollbackContext);
        await this.finishSuccessfulSave(
          this.editCarePlanId,
          'Care Plan updated successfully',
        );
        return;
      }

      const createResponse = await firstValueFrom(
        this.carePlanService.addCarePlan(request),
      );
      const createdCarePlanId =
        createResponse.data ?? (await this.resolveCreatedCarePlanId(request));

      if (!createdCarePlanId) {
        throw new Error(
          'Care plan summary was created, but the new care plan id could not be resolved for activity linking.',
        );
      }

      rollbackContext.createdCarePlanId = createdCarePlanId;
      this.activateCreatedDraftCarePlan(createdCarePlanId, request);

      await this.applyActivityChanges(createdCarePlanId, rollbackContext);
      await this.applyAttachmentUploads(createdCarePlanId, rollbackContext);

      await this.finishSuccessfulSave(
        createdCarePlanId,
        'Care Plan created successfully',
      );
    } catch (error) {
      await this.rollbackFailedSave(rollbackContext);
      this.showSaveError(error, rollbackContext);
    } finally {
      this.isLoading = false;
    }
  }

  openAppointmentLinkModal() {
    if (this.isReadOnly) {
      return;
    }

    this.isAppointmentLinkVisible = true;
    this.loadAvailableAppointments();
  }

  closeAppointmentLinkModal() {
    this.isAppointmentLinkVisible = false;
  }

  openAppointmentCreateModal() {
    if (this.isReadOnly) {
      return;
    }

    this.appointmentService.loadAppointmentStatuses();
    this.calendarEventsService.addCalendarEventModalData = {
      start: new Date(),
      patients: [this.getCurrentPatientResource()],
      personnel: this.getAppointmentPersonnelResources(),
      departments: DEPARTMENT_LIST,
      recurrentType: this.appointmentDropdownData?.recurrentType ?? [],
      appointmentServiceTypeList:
        this.appointmentDropdownData?.serviceTypes ?? [],
      appointmentReasonList: this.appointmentDropdownData?.reasons ?? [],
      resourceId: this.patientService.patientIDsignal(),
      carePlanPeriodEnd: this.getCarePlanPeriodEndIso(),
      onCreateSuccess: (appointmentId: number, appointmentEvent?: EventInput) => {
        void this.stageCreatedAppointment(appointmentId, appointmentEvent);
      },
    };
    this.calendarEventsService.addCalendarEventModalSub.next(true);
  }

  openMedicationLinkModal() {
    if (this.isReadOnly) {
      return;
    }

    this.isMedicationLinkVisible = true;
    this.loadAvailableMedications();
  }

  closeMedicationLinkModal() {
    this.isMedicationLinkVisible = false;
    this.pendingMedicationLinkAfterCreate = false;
  }

  openMedicationAddModal() {
    if (this.isReadOnly) {
      return;
    }

    this.pendingMedicationLinkAfterCreate = true;
    this.currentMedicationService.medicationModalData = {
      carePlanPeriodEnd: this.getCarePlanPeriodEndIso(),
    };
    this.currentMedicationService.medicationEditSub.next(undefined);
    this.currentMedicationService.medicationOpenSub.next(true);
  }

  closeMedicationAddModal() {
    this.pendingMedicationLinkAfterCreate = false;
    this.currentMedicationService.medicationModalData = {};
    this.currentMedicationService.medicationOpenSub.next(false);
  }

  openServiceRequestLinkModal() {
    if (this.isReadOnly) {
      return;
    }

    this.isServiceRequestLinkVisible = true;
    this.loadAvailableServiceRequests();
  }

  closeServiceRequestLinkModal() {
    this.isServiceRequestLinkVisible = false;
  }

  openServiceRequestAddModal() {
    if (this.isReadOnly) {
      return;
    }

    this.isServiceRequestAddVisible = true;
  }

  closeServiceRequestAddModal() {
    this.isServiceRequestAddVisible = false;
  }

  openAttachmentUploadModal() {
    if (!this.canManageAttachments) {
      return;
    }

    this.attachmentForm.reset({
      date: null,
      description: null,
      file: null,
    });
    this.attachmentFile = undefined;
    if (this.attachmentFileInput?.nativeElement) {
      this.attachmentFileInput.nativeElement.value = '';
    }
    this.isAttachmentUploadVisible = true;
  }

  closeAttachmentUploadModal() {
    this.isAttachmentUploadVisible = false;
    this.isAttachmentUploading = false;
    this.attachmentFile = undefined;
    this.attachmentForm.reset({
      date: null,
      description: null,
      file: null,
    });
    if (this.attachmentFileInput?.nativeElement) {
      this.attachmentFileInput.nativeElement.value = '';
    }
  }

  closePdfPreview() {
    this.isPdfPreviewVisible = false;
    this.pdfPreviewFileName = undefined;
    this.revokeAttachmentPreviewUrl();
  }

  async onAttachmentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files ?? []);

    if (files.length === 0) {
      return;
    }

    for (const file of files) {
      const validationResult = await validateUpload(file);
      if (!validationResult.ok) {
        this.swalToastService.toastError(validationResult.error!);
        continue;
      }

      if (
        !this.editCarePlanId ||
        this.createdDraftCarePlanId === this.editCarePlanId
      ) {
        this.stagePendingAttachmentUpload(file);
        continue;
      }

      await this.uploadExistingAttachment(file);
    }

    if (this.attachmentFileInput?.nativeElement) {
      this.attachmentFileInput.nativeElement.value = '';
    }
  }

  submitAttachmentUpload() {
    if (!this.canManageAttachments) {
      return;
    }

    this.attachmentForm.markAllAsTouched();

    if (!this.attachmentFile || this.attachmentForm.invalid) {
      return;
    }

    if (
      !this.editCarePlanId ||
      this.createdDraftCarePlanId === this.editCarePlanId
    ) {
      this.stagePendingAttachmentUpload(this.attachmentFile);
      return;
    }

    this.isAttachmentUploading = true;
    this.carePlanService
      .uploadCarePlanAttachment(
        this.editCarePlanId,
        this.attachmentFile,
        this.attachmentForm.value.date,
        this.attachmentForm.value.description,
      )
      .subscribe({
        next: (response) => {
          this.swalToastService.toastSuccess(
            response.message ||
              this.translateMessage('Attachment uploaded successfully'),
          );
          this.closeAttachmentUploadModal();
          this.loadAttachments(this.editCarePlanId!);
        },
        error: (err) => {
          this.isAttachmentUploading = false;
          this.swalToastService.toastError(
            err?.error?.message ||
              this.translateMessage('Unable to upload attachment'),
          );
        },
      });
  }

  private stagePendingAttachmentUpload(file: File) {
    this.attachmentRows = [
      ...this.attachmentRows,
      {
        uiId: this.createUiId(`pending-attachment-${file.name}`),
        date: null,
        description: null,
        fileName: file.name,
        contentType: file.type || null,
        size: file.size,
        createdDate: null,
        createdByUserId: null,
        isSubmitted: false,
        file,
        isPendingUpload: true,
        objectUrl: URL.createObjectURL(file),
      },
    ];

    this.updateDirtyState();
    this.swalToastService.toastSuccess(
      this.translateMessage('Attachment added to the care plan'),
    );
  }

  private async uploadExistingAttachment(file: File) {
    if (!this.editCarePlanId) {
      return;
    }

    this.isAttachmentUploading = true;

    try {
      const response = await firstValueFrom(
        this.carePlanService.uploadCarePlanAttachment(this.editCarePlanId, file),
      );
      this.swalToastService.toastSuccess(
        response.message || this.translateMessage('Attachment uploaded successfully'),
      );
      this.loadAttachments(this.editCarePlanId);
    } catch (err) {
      this.swalToastService.toastError(
        (err as any)?.error?.message ||
          this.translateMessage('Unable to upload attachment'),
      );
    } finally {
      this.isAttachmentUploading = false;
    }
  }

  downloadAttachment(row: CarePlanAttachmentRow) {
    if (!this.editCarePlanId || row.isPendingUpload || !row.id) {
      return;
    }

    this.carePlanService
      .downloadCarePlanAttachment(this.editCarePlanId, row.id)
      .subscribe({
        next: (response) => {
          const payload = this.mapAttachmentDownloadPayload(response, row);
          saveAs(payload.blob, payload.fileName);
        },
        error: (err) => {
          this.swalToastService.toastError(
            err?.error?.Message ||
              err?.error?.message ||
              this.translateMessage('Unable to download attachment'),
          );
        },
      });
  }

  viewAttachment(row: CarePlanAttachmentRow) {
    if (row.isPendingUpload && row.objectUrl) {
      this.previewAttachmentObjectUrl(
        row.objectUrl,
        row.contentType,
        row.fileName,
        false,
      );
      return;
    }

    if (!this.editCarePlanId || !row.id || !this.canPreviewAttachment(row)) {
      return;
    }

    this.carePlanService
      .downloadCarePlanAttachment(this.editCarePlanId, row.id)
      .subscribe({
        next: (response) => {
          const payload = this.mapAttachmentDownloadPayload(response, row);
          const objectUrl = URL.createObjectURL(payload.blob);
          this.previewAttachmentObjectUrl(
            objectUrl,
            payload.blob.type || row.contentType,
            payload.fileName,
            true,
          );
        },
        error: (err) => {
          this.swalToastService.toastError(
            err?.error?.Message ||
              err?.error?.message ||
              this.translateMessage('Unable to download attachment'),
          );
        },
      });
  }

  deleteAttachment(row: CarePlanAttachmentRow) {
    if (!this.canManageAttachments) {
      return;
    }

    if (row.isPendingUpload || !row.id || !this.editCarePlanId) {
      this.attachmentRows = this.attachmentRows.filter(
        (item) => item.uiId !== row.uiId,
      );
      if (row.objectUrl) {
        URL.revokeObjectURL(row.objectUrl);
      }
      this.updateDirtyState();
      this.swalToastService.toastSuccess(
        this.translateMessage('Attachment removed from the care plan'),
      );
      return;
    }

    Swal.fire({
      text: `${this.translate.instant('deletePlaceholder1')} ${this.translate.instant('Attachment')}?`,
      showCancelButton: true,
      cancelButtonText: this.translate.instant('Cancel'),
      confirmButtonText: this.translate.instant('Ok'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      const attachmentId = row.id!;
      this.carePlanService
        .deleteCarePlanAttachment(this.editCarePlanId!, attachmentId)
        .subscribe({
          next: (response) => {
            this.swalToastService.toastSuccess(
              response.message ||
                this.translateMessage('Attachment deleted successfully'),
            );
            this.loadAttachments(this.editCarePlanId!);
          },
          error: (err) => {
            this.swalToastService.toastError(
              err?.error?.message ||
                this.translateMessage('Unable to delete attachment'),
            );
          },
        });
    });
  }

  linkExistingServiceRequest(row: AvailableServiceRequestRow) {
    const stagedRow = this.createServiceRequestRowFromAvailable(
      row,
      false,
      row.activityState,
    );

    this.linkedServiceRequestRows = [...this.linkedServiceRequestRows, stagedRow];
    this.closeServiceRequestLinkModal();
    this.loadAvailableServiceRequests();
    this.updateDirtyState();
    this.swalToastService.toastSuccess(
      'Service request added to the care plan',
    );
  }

  async handleCreatedServiceRequest(serviceRequestId: number) {
    this.closeServiceRequestAddModal();
    await this.stageCreatedServiceRequest(serviceRequestId);
  }

  linkExistingAppointment(row: AvailableAppointmentRow) {
    const boundaryError = this.getAppointmentBoundaryValidationMessage(row);

    if (boundaryError) {
      this.swalToastService.toastError(boundaryError);
      return;
    }

    const stagedRow = this.createAppointmentRowFromAvailable(
      row,
      false,
      'Planned',
    );

    this.linkedAppointmentRows = [...this.linkedAppointmentRows, stagedRow];
    this.closeAppointmentLinkModal();
    this.loadAvailableAppointments();
    this.updateDirtyState();
    this.swalToastService.toastSuccess('Appointment added to the care plan');
  }

  async linkMedicationFromCurrent(
    row: AvailableMedicationRow,
    actionType: 'continue' | 'stop',
  ) {
    if (actionType === 'continue') {
      const boundaryError = this.getMedicationBoundaryValidationMessage(
        row.endDateTime,
      );

      if (boundaryError) {
        this.swalToastService.toastError(boundaryError);
        return;
      }

      const stagedRow = this.createMedicationRowFromAvailable(
        row,
        'continue',
        false,
        row.medicationId,
      );

      this.linkedMedicationRows = [...this.linkedMedicationRows, stagedRow];
      this.closeMedicationLinkModal();
      this.loadAvailableMedications();
      this.updateDirtyState();
      this.swalToastService.toastSuccess('Medication added to the care plan');
      return;
    }

    const carePlanPeriodEnd = this.getCarePlanPeriodEndDate();
    if (carePlanPeriodEnd && new Date() > carePlanPeriodEnd) {
      this.swalToastService.toastError(
        this.translateMessage(
          'The care plan end date cannot be before one or more linked medications.',
        ),
      );
      return;
    }

    await this.createStopMedicationAndStage(row);
  }

  async editAppointmentRow(row: LinkedAppointmentRow) {
    if (this.isReadOnly) {
      return;
    }

    try {
      const appointmentEvent = await this.buildAppointmentEvent(row);

      if (!appointmentEvent) {
        this.swalToastService.toastError('Unable to edit appointment');
        return;
      }

      this.calendarEventsService.addCalendarEventModalData = {
        event: appointmentEvent as any,
        patients: [this.getCurrentPatientResource()],
        personnel: this.getAppointmentPersonnelResources(),
        departments: DEPARTMENT_LIST,
        recurrentType: this.appointmentDropdownData?.recurrentType ?? [],
        appointmentServiceTypeList:
          this.appointmentDropdownData?.serviceTypes ?? [],
        appointmentReasonList: this.appointmentDropdownData?.reasons ?? [],
        resourceId: this.patientService.patientIDsignal(),
        carePlanPeriodEnd: this.getCarePlanPeriodEndIso(),
        onUpdateSuccess: (appointmentId: number, updatedEvent?: EventInput) => {
          this.handleUpdatedAppointment(appointmentId, updatedEvent);
        },
      };
      this.calendarEventsService.addCalendarEventModalSub.next(true);
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(error, 'Unable to edit appointment'),
      );
    }
  }

  async viewAppointmentRow(row: LinkedAppointmentRow) {
    try {
      const appointmentEvent = await this.buildAppointmentEvent(row);

      if (!appointmentEvent) {
        this.swalToastService.toastError('Unable to view appointment');
        return;
      }

      const normalizedEvent =
        this.normalizeAppointmentEventForEditor(appointmentEvent);
      const extendedProps = normalizedEvent.extendedProps ?? {};

      this.calendarEventsService.viewCalendarEventModalData = {
        event: normalizedEvent as any,
        patients: Array.isArray(extendedProps['patients'])
          ? extendedProps['patients']
          : [],
        personnel: Array.isArray(extendedProps['personnel'])
          ? extendedProps['personnel']
          : [],
        departments: Array.isArray(extendedProps['department'])
          ? extendedProps['department']
          : [],
        recurrentType: extendedProps['recurrentTypeId'] ?? 1,
        appointmentStatusList: this.getAppointmentStatusesByType(1),
        appointmentServiceTypeList:
          this.appointmentDropdownData?.serviceTypes ?? [],
        appointmentReasonList: this.appointmentDropdownData?.reasons ?? [],
        slotStatusList: this.getAppointmentStatusesByType(2),
        participantStatusList: this.getAppointmentStatusesByType(3),
        isReadOnly: true,
      };
      this.calendarEventsService.viewCalendarEventModalSub.next(true);
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(error, 'Unable to view appointment'),
      );
    }
  }

  async viewMedicationRow(row: LinkedMedicationRow) {
    try {
      const medication = await this.resolveMedicationForView(row);

      if (!medication) {
        this.swalToastService.toastError('Unable to view medication');
        return;
      }

      this.pendingMedicationLinkAfterCreate = false;
      this.currentMedicationService.medicationModalData = {
        isReadOnly: true,
      };
      this.currentMedicationService.medicationEditSub.next(medication);
      this.currentMedicationService.medicationOpenSub.next(true);
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(error, 'Unable to view medication'),
      );
    }
  }

  async removeAppointmentRow(row: LinkedAppointmentRow) {
    if (!row.linkId && row.deleteOnCancel) {
      await this.cleanupDraftAppointment(row.appointmentId);
    }

    this.linkedAppointmentRows = this.linkedAppointmentRows.filter(
      (item) => item.uiId !== row.uiId,
    );

    if (this.isAppointmentLinkVisible) {
      this.loadAvailableAppointments();
    }

    this.updateDirtyState();
  }

  async removeMedicationRow(row: LinkedMedicationRow) {
    if (!row.linkId && row.deleteOnCancel) {
      await this.cleanupDraftMedication(row);
    }

    this.linkedMedicationRows = this.linkedMedicationRows.filter(
      (item) => item.uiId !== row.uiId,
    );

    if (this.isMedicationLinkVisible) {
      this.loadAvailableMedications();
    }

    this.updateDirtyState();
  }

  async removeServiceRequestRow(row: LinkedServiceRequestRow) {
    if (!row.linkId && row.deleteOnCancel) {
      await this.cleanupDraftServiceRequest(row.serviceRequestId);
    }

    this.linkedServiceRequestRows = this.linkedServiceRequestRows.filter(
      (item) => item.uiId !== row.uiId,
    );

    if (this.isServiceRequestLinkVisible) {
      this.loadAvailableServiceRequests();
    }

    this.updateDirtyState();
  }

  private populateForm(data?: ICarePlanDetailed) {
    this.carePlanSubmitted = false;
    this.loadedCarePlanSnapshot = data;
    this.createdDraftCarePlanId = undefined;
    this.isSubmittedRevisionDraft = false;
    this.clearDraftResourceTracking();

    this.suppressDirtyTracking = true;

    if (!data) {
      this.isEdit = false;
      this.editCarePlanId = undefined;
      this.carePlanForm.reset({
        statusId: null,
        intentId: null,
        categoryId: null,
        title: null,
        description: null,
        notes: null,
        period: null,
      });
      this.resetActivities();
      this.resetAttachments();
      this.applyCreateDefaults();
      this.baselineRequestSnapshot = this.buildCarePlanRequest();
      this.captureActivityBaseline();
      this.captureAttachmentBaseline();
      this.suppressDirtyTracking = false;
      this.updateDirtyState();
      return;
    }

    this.isEdit = true;
    this.editCarePlanId = data.id;
    this.carePlanForm.reset({
      statusId: this.toNumber(data.statusId),
      intentId: this.toNumber(data.intentId),
      categoryId: this.toNumber(data.categoryId),
      title: data.title ?? null,
      description: data.description ?? null,
      notes: data.notes ?? null,
      period:
        data.periodStart || data.periodEnd
          ? [this.toDate(data.periodStart), this.toDate(data.periodEnd)]
          : null,
    });
    this.baselineRequestSnapshot = this.buildRequestFromDetailed(data);
    this.suppressDirtyTracking = false;
    this.loadActivities(data.id);
    this.loadAttachments(data.id);
  }

  private loadDropdownData() {
    this.isDropdownLoading = true;
    this.carePlanService.getDropdownData().subscribe({
      next: (response) => {
        this.dropdownData = response.data;
        this.isDropdownLoading = false;

        if (!this.isEdit) {
          this.suppressDirtyTracking = true;
          this.applyCreateDefaults();
          this.baselineRequestSnapshot = this.buildCarePlanRequest();
          this.suppressDirtyTracking = false;
          this.updateDirtyState();
        }
      },
      error: () => {
        this.isDropdownLoading = false;
        this.swalToastService.toastError(
          'Unable to retrieve care plan dropdown data',
        );
      },
    });
  }

  private loadAppointmentLookups() {
    this.appointmentService.loadAppointmentStatuses();
    this.appointmentService.getDropdownData().subscribe({
      next: (response) => {
        this.appointmentDropdownData = response.data;
        this.appointmentStatuses =
          response.data?.statuses?.filter((status) => status.typeId === 1) ?? [];
        this.refreshAppointmentLabels();
      },
      error: () => {
        this.swalToastService.toastError(
          'Unable to retrieve appointment dropdown data',
        );
      },
    });
  }

  private loadActivities(carePlanId: number) {
    this.isActivitiesLoading = true;
    this.carePlanService.getActivities(carePlanId).subscribe({
      next: (response) => {
        this.mapActivities(response.data);
        this.captureActivityBaseline();
        this.isActivitiesLoading = false;
        this.updateDirtyState();
      },
      error: (err) => {
        this.resetActivities();
        this.captureActivityBaseline();
        this.isActivitiesLoading = false;
        this.updateDirtyState();
        this.swalToastService.toastError(
          err?.error?.message || 'Unable to retrieve care plan activities',
        );
      },
    });
  }

  private loadAttachments(carePlanId: number) {
    this.isAttachmentsLoading = true;
    this.carePlanService.getCarePlanAttachments(carePlanId).subscribe({
      next: (response) => {
        this.attachmentRows = (response.data ?? []).map((row) =>
          this.mapAttachmentRow(row),
        );
        this.captureAttachmentBaseline();
        this.isAttachmentsLoading = false;
        this.updateDirtyState();
      },
      error: (err) => {
        this.attachmentRows = [];
        this.captureAttachmentBaseline();
        this.isAttachmentsLoading = false;
        this.updateDirtyState();
        this.swalToastService.toastError(
          err?.error?.message ||
            this.translateMessage('Unable to retrieve attachments'),
        );
      },
    });
  }

  private loadAvailableAppointments() {
    const patientId = this.patientService.patientIDsignal();

    if (!patientId) {
      this.availableAppointmentRows = [];
      return;
    }

    this.isAvailableAppointmentsLoading = true;
    forkJoin({
      dropdownData: this.appointmentDropdownData
        ? of(null)
        : this.appointmentService.getDropdownData(),
      appointmentsResponse: this.appointmentService.getAppointments(
        this.getAppointmentScope(),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ dropdownData, appointmentsResponse }) => {
          if (dropdownData && 'data' in dropdownData) {
            this.appointmentDropdownData = dropdownData.data;
          }

          const appointments = Array.isArray(appointmentsResponse.data)
            ? appointmentsResponse.data
            : [];

          this.availableAppointmentRows = this.mapAvailableAppointments(
            appointments,
            patientId,
          );
          this.isAvailableAppointmentsLoading = false;
        },
        error: (err) => {
          this.availableAppointmentRows = [];
          this.isAvailableAppointmentsLoading = false;
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to retrieve appointments',
          );
        },
      });
  }

  private loadAvailableMedications() {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId() || undefined;

    if (!patientId) {
      this.availableMedicationRows = [];
      return;
    }

    this.isAvailableMedicationsLoading = true;
    this.currentMedicationService
      .getCurrentMedication(patientId, visitId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableMedicationRows = this.mapAvailableMedications(
            response.data?.getAll,
          );
          this.isAvailableMedicationsLoading = false;
        },
        error: (err) => {
          this.availableMedicationRows = [];
          this.isAvailableMedicationsLoading = false;
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to retrieve current medications',
          );
        },
      });
  }

  private loadAvailableServiceRequests() {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId() || undefined;

    if (!patientId) {
      this.availableServiceRequestRows = [];
      return;
    }

    this.isAvailableServiceRequestsLoading = true;
    this.serviceRequestService
      .getServiceRequests(patientId, visitId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableServiceRequestRows = this.mapAvailableServiceRequests(
            response.data,
          );
          this.isAvailableServiceRequestsLoading = false;
        },
        error: (err) => {
          this.availableServiceRequestRows = [];
          this.isAvailableServiceRequestsLoading = false;
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to retrieve service requests',
          );
        },
      });
  }

  private mapActivities(data: ICarePlanActivitiesDetailed) {
    this.linkedAppointmentRows = [
      ...data.plannedAppointments.map((item) =>
        this.mapAppointmentActivityRow(item),
      ),
      ...data.performedAppointments.map((item) =>
        this.mapAppointmentActivityRow(item),
      ),
    ];

    this.linkedMedicationRows = [
      ...data.plannedMedications.map((item) =>
        this.mapMedicationActivityRow(item),
      ),
      ...data.performedMedications.map((item) =>
        this.mapMedicationActivityRow(item),
      ),
    ];

    this.linkedServiceRequestRows = [
      ...data.plannedServiceRequests.map((item) =>
        this.mapServiceRequestActivityRow(item),
      ),
      ...data.performedServiceRequests.map((item) =>
        this.mapServiceRequestActivityRow(item),
      ),
    ];
  }

  private mapAppointmentActivityRow(
    activity: ICarePlanAppointmentActivity,
  ): LinkedAppointmentRow {
    return {
      uiId: this.createUiId(`link-appointment-${activity.linkId}`),
      linkId: activity.linkId,
      appointmentId: activity.appointmentId,
      appointmentStatusId: activity.appointmentStatusId,
      appointmentServiceTypeId: activity.appointmentServiceTypeId,
      appointmentReasonId: activity.appointmentReasonId,
      activityState: this.capitalize(activity.activityState),
      status: this.getAppointmentStatusLabel(activity.appointmentStatusId),
      serviceType: this.getAppointmentServiceTypeLabel(
        activity.appointmentServiceTypeId,
      ),
      reason: this.getAppointmentReasonLabel(activity.appointmentReasonId),
      description: activity.description || '-',
      nextSlotStart: this.formatDateTime(activity.nextSlotStart),
      lastSlotEnd: this.formatDateTime(activity.lastSlotEnd),
      nextSlotStartRaw: activity.nextSlotStart,
      lastSlotEndRaw: activity.lastSlotEnd,
      deleteOnCancel: false,
      appointmentEvent: null,
    };
  }

  private mapMedicationActivityRow(
    activity: ICarePlanMedicationActivity,
  ): LinkedMedicationRow {
    const actionType = this.normalizeMedicationActionType(
      activity.medicationActionType,
    );

    return {
      uiId: this.createUiId(`link-medication-${activity.linkId}`),
      linkId: activity.linkId,
      medicationId: activity.medicationId,
      sourceMedicationId: activity.medicationId,
      activityState: this.capitalize(activity.activityState),
      actionTypeRaw: actionType,
      medicationActionType: this.capitalize(actionType),
      medication:
        activity.productName && activity.productDescription
          ? `${activity.productName} - ${activity.productDescription}`
          : activity.productName || activity.activeIngredient || '-',
      timing: this.formatMedicationTiming(
        activity.onSetDateTime,
        activity.endDateTime,
      ),
      assignedDoctor: activity.assignedDoctorFullName || '-',
      onSetDateTime: activity.onSetDateTime,
      endDateTime: activity.endDateTime,
      deleteOnCancel: false,
    };
  }

  private mapServiceRequestActivityRow(
    activity: ICarePlanServiceRequestActivity,
  ): LinkedServiceRequestRow {
    return {
      uiId: this.createUiId(`link-service-request-${activity.linkId}`),
      linkId: activity.linkId,
      serviceRequestId: activity.serviceRequestId,
      activityState: this.capitalize(activity.activityState),
      status: activity.statusDisplay || activity.statusCode || '-',
      intent: activity.intentDisplay || activity.intentCode || '-',
      priority: activity.priorityDisplay || activity.priorityCode || '-',
      category: activity.categoryDisplay || activity.categoryCode || '-',
      procedure:
        activity.procedureCodeDisplay || activity.procedureCode || '-',
      occurrence: this.formatServiceRequestOccurrence(
        activity.occurrenceStart,
        activity.occurrenceEnd,
      ),
      description: activity.description || activity.notes || '-',
      deleteOnCancel: false,
    };
  }

  private mapAvailableAppointments(
    appointments: IAppointment[],
    patientId: string,
  ): AvailableAppointmentRow[] {
    const linkedAppointmentIds = new Set(
      this.linkedAppointmentRows.map((row) => row.appointmentId),
    );
    const now = new Date();

    return appointments
      .filter(
        (appointment) =>
          appointment.participants?.some(
            (participant) =>
              participant.actor?.patientId?.toString() === patientId,
          ) && !linkedAppointmentIds.has(appointment.id),
      )
      .map((appointment) => {
        const futureSlots = (appointment.slots ?? [])
          .filter((slot) => new Date(slot.end) > now)
          .sort(
            (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
          );

        return {
          appointmentId: appointment.id,
          appointmentStatusId: appointment.appointmentStatusId,
          appointmentServiceTypeId: appointment.appointmentServiceTypeId,
          appointmentReasonId: appointment.appointmentReasonId,
          when: this.formatAppointmentRange(
            futureSlots[0]?.start,
            futureSlots[0]?.end,
          ),
          status: this.getAppointmentStatusLabel(
            appointment.appointmentStatusId,
          ),
          serviceType: this.getAppointmentServiceTypeLabel(
            appointment.appointmentServiceTypeId,
          ),
          reason: this.getAppointmentReasonLabel(
            appointment.appointmentReasonId,
          ),
          description: appointment.description || '-',
          nextSlotStartRaw: futureSlots[0]?.start ?? null,
          lastSlotEndRaw:
            futureSlots.length > 0
              ? futureSlots[futureSlots.length - 1]?.end ?? null
              : null,
        };
      })
      .filter((appointment) => appointment.when !== '-')
      .sort((a, b) => a.when.localeCompare(b.when));
  }

  private mapAvailableMedications(
    medications?: IMedicationDetailed[],
  ): AvailableMedicationRow[] {
    const linkedSourceMedicationIds = new Set(
      this.linkedMedicationRows.map((row) => row.sourceMedicationId),
    );

    return (medications ?? [])
      .filter(
        (medication) =>
          !!medication.id && !linkedSourceMedicationIds.has(medication.id),
      )
      .map((medication) => ({
        medicationId: medication.id!,
        medication:
          medication.productDescription ||
          medication.productName ||
          medication.activeIngredient ||
          '-',
        timing: this.formatMedicationTiming(
          medication.onSetDateTime,
          medication.endDateTime,
        ),
        assignedDoctor: medication.assignedDoctorFullName || '-',
        onSetDateTime: medication.onSetDateTime,
        endDateTime: medication.endDateTime,
        replacementId: medication.replacementId,
      }));
  }

  private mapAvailableServiceRequests(
    serviceRequests?: IServiceRequestDetailed[],
  ): AvailableServiceRequestRow[] {
    const linkedServiceRequestIds = new Set(
      this.linkedServiceRequestRows.map((row) => row.serviceRequestId),
    );

    return (serviceRequests ?? [])
      .filter(
        (serviceRequest) =>
          !!serviceRequest.id &&
          !linkedServiceRequestIds.has(serviceRequest.id),
      )
      .map((serviceRequest) => ({
        serviceRequestId: serviceRequest.id,
        activityState: this.deriveServiceRequestState(
          serviceRequest.statusCode,
          serviceRequest.occurrenceEnd,
        ),
        status: serviceRequest.statusDisplay || serviceRequest.statusCode || '-',
        intent: serviceRequest.intentDisplay || serviceRequest.intentCode || '-',
        priority:
          serviceRequest.priorityDisplay || serviceRequest.priorityCode || '-',
        category:
          serviceRequest.categoryDisplay || serviceRequest.categoryCode || '-',
        procedure:
          serviceRequest.procedureCodeDisplay ||
          serviceRequest.procedureCode ||
          '-',
        occurrence: this.formatServiceRequestOccurrence(
          serviceRequest.occurrenceStart,
          serviceRequest.occurrenceEnd,
        ),
        description: serviceRequest.description || serviceRequest.notes || '-',
      }))
      .sort((a, b) => a.occurrence.localeCompare(b.occurrence));
  }

  private createAppointmentRowFromAvailable(
    row: AvailableAppointmentRow,
    deleteOnCancel: boolean,
    state: string,
  ): LinkedAppointmentRow {
    return {
      uiId: this.createUiId(`draft-appointment-${row.appointmentId}`),
      appointmentId: row.appointmentId,
      appointmentStatusId: row.appointmentStatusId,
      appointmentServiceTypeId: row.appointmentServiceTypeId,
      appointmentReasonId: row.appointmentReasonId,
      activityState: state,
      status: row.status,
      serviceType: row.serviceType,
      reason: row.reason,
      description: row.description,
      nextSlotStartRaw: row.nextSlotStartRaw,
      lastSlotEndRaw: row.lastSlotEndRaw,
      nextSlotStart: row.when,
      lastSlotEnd: this.formatDateTime(row.lastSlotEndRaw),
      deleteOnCancel,
      appointmentEvent: null,
    };
  }

  private createAppointmentRowFromEventInput(
    appointmentId: number,
    eventData: EventInput,
    deleteOnCancel: boolean,
    state: string,
  ): LinkedAppointmentRow {
    const nextSlotStartRaw = this.toEventDateIso(eventData.start);
    const lastSlotEndRaw = this.toEventDateIso(eventData.end);

    return {
      uiId: this.createUiId(`draft-appointment-${appointmentId}`),
      appointmentId,
      appointmentStatusId: eventData.extendedProps?.['appointmentStatusId'] ?? 3,
      appointmentServiceTypeId:
        eventData.extendedProps?.['appointmentServiceTypeId'] ?? null,
      appointmentReasonId:
        eventData.extendedProps?.['appointmentReasonId'] ?? null,
      activityState: state,
      status: this.getAppointmentStatusLabel(
        eventData.extendedProps?.['appointmentStatusId'] ?? 3,
      ),
      serviceType: this.getAppointmentServiceTypeLabel(
        eventData.extendedProps?.['appointmentServiceTypeId'] ?? null,
      ),
      reason: this.getAppointmentReasonLabel(
        eventData.extendedProps?.['appointmentReasonId'] ?? null,
      ),
      description: (typeof eventData.title === 'string' ? eventData.title : '-') || '-',
      nextSlotStartRaw,
      lastSlotEndRaw,
      nextSlotStart: this.formatAppointmentRange(
        nextSlotStartRaw ?? undefined,
        lastSlotEndRaw ?? undefined,
      ),
      lastSlotEnd: this.formatDateTime(lastSlotEndRaw),
      deleteOnCancel,
      appointmentEvent: { ...eventData, id: String(appointmentId) },
    };
  }

  private createMedicationRowFromAvailable(
    row: AvailableMedicationRow,
    actionType: 'continue' | 'stop',
    deleteOnCancel: boolean,
    sourceMedicationId: number,
  ): LinkedMedicationRow {
    return {
      uiId: this.createUiId(`draft-medication-${row.medicationId}`),
      medicationId: row.medicationId,
      sourceMedicationId,
      activityState: actionType === 'stop' ? 'Performed' : 'Planned',
      actionTypeRaw: actionType,
      medicationActionType: this.capitalize(actionType),
      medication: row.medication,
      timing: row.timing,
      assignedDoctor: row.assignedDoctor,
      onSetDateTime: row.onSetDateTime,
      endDateTime: row.endDateTime,
      deleteOnCancel,
    };
  }

  private createServiceRequestRowFromAvailable(
    row: AvailableServiceRequestRow,
    deleteOnCancel: boolean,
    state: string,
  ): LinkedServiceRequestRow {
    return {
      uiId: this.createUiId(`draft-service-request-${row.serviceRequestId}`),
      serviceRequestId: row.serviceRequestId,
      activityState: this.capitalize(state),
      status: row.status,
      intent: row.intent,
      priority: row.priority,
      category: row.category,
      procedure: row.procedure,
      occurrence: row.occurrence,
      description: row.description,
      deleteOnCancel,
    };
  }

  private async stageCreatedAppointment(
    appointmentId: number,
    appointmentEvent?: EventInput,
  ) {
    this.createdDraftAppointmentIds.add(appointmentId);

    try {
      const stagedAppointment = appointmentEvent
        ? this.createAppointmentRowFromEventInput(
            appointmentId,
            appointmentEvent,
            true,
            'Planned',
          )
        : undefined;

      if (stagedAppointment) {
        const boundaryError =
          this.getAppointmentBoundaryValidationMessage(stagedAppointment);

        if (boundaryError) {
          await this.cleanupDraftAppointment(appointmentId);
          this.swalToastService.toastError(boundaryError);
          return;
        }
      }

      const stagedRow = stagedAppointment
        ? stagedAppointment
        : {
            uiId: this.createUiId(`draft-appointment-${appointmentId}`),
            appointmentId,
            activityState: 'Planned',
            status: '-',
            serviceType: '-',
            reason: '-',
            description: '-',
            nextSlotStart: '-',
            lastSlotEnd: '-',
            nextSlotStartRaw: null,
            lastSlotEndRaw: null,
            deleteOnCancel: true,
            appointmentEvent: null,
          };

      this.linkedAppointmentRows = [...this.linkedAppointmentRows, stagedRow];
      this.updateDirtyState();
      this.swalToastService.toastSuccess(
        'Appointment created and added to the care plan',
      );
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(
          error,
          'Appointment was created, but it could not be staged in the care plan',
        ),
      );
    } finally {
      if (this.isAppointmentLinkVisible) {
        this.loadAvailableAppointments();
      }
    }
  }

  private async stageCreatedMedication(medicationId: number) {
    this.createdDraftCurrentMedicationIds.add(medicationId);

    try {
      const currentMedications = await this.fetchCurrentMedications();
      const createdMedication = this.mapAvailableMedications(
        currentMedications.filter(
          (medication) => medication.id === medicationId,
        ),
      )[0];

      if (createdMedication) {
        const boundaryError = this.getMedicationBoundaryValidationMessage(
          createdMedication.endDateTime,
        );

        if (boundaryError) {
          await this.cleanupDraftMedication({
            uiId: this.createUiId(`draft-medication-${medicationId}`),
            medicationId,
            sourceMedicationId: medicationId,
            activityState: 'Planned',
            actionTypeRaw: 'continue',
            medicationActionType: 'Continue',
            medication: createdMedication.medication,
            timing: createdMedication.timing,
            assignedDoctor: createdMedication.assignedDoctor,
            onSetDateTime: createdMedication.onSetDateTime,
            endDateTime: createdMedication.endDateTime,
            deleteOnCancel: true,
          });
          this.swalToastService.toastError(boundaryError);
          return;
        }
      }

      const stagedRow = createdMedication
        ? this.createMedicationRowFromAvailable(
            createdMedication,
            'continue',
            true,
            createdMedication.medicationId,
          )
        : {
            uiId: this.createUiId(`draft-medication-${medicationId}`),
            medicationId,
            sourceMedicationId: medicationId,
            activityState: 'Planned',
            actionTypeRaw: 'continue' as const,
            medicationActionType: 'Continue',
            medication: '-',
            timing: '-',
            assignedDoctor: '-',
            onSetDateTime: null,
            endDateTime: null,
            deleteOnCancel: true,
          };

      this.linkedMedicationRows = [...this.linkedMedicationRows, stagedRow];
      this.closeMedicationAddModal();
      this.updateDirtyState();
      this.swalToastService.toastSuccess(
        'Medication created and added to the care plan',
      );
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(
          error,
          'Medication was created, but it could not be staged in the care plan',
        ),
      );
    } finally {
      if (this.isMedicationLinkVisible) {
        this.loadAvailableMedications();
      }
    }
  }

  private handleUpdatedAppointment(
    appointmentId: number,
    updatedEvent?: EventInput,
  ) {
    if (!updatedEvent) {
      return;
    }

    const existingRow = this.linkedAppointmentRows.find(
      (row) => row.appointmentId === appointmentId,
    );

    if (!existingRow) {
      return;
    }

    const nextRow: LinkedAppointmentRow = {
      ...this.createAppointmentRowFromEventInput(
        appointmentId,
        updatedEvent,
        existingRow.deleteOnCancel,
        existingRow.activityState,
      ),
      linkId: existingRow.linkId,
    };

    this.linkedAppointmentRows = this.linkedAppointmentRows.map((row) =>
      row.appointmentId === appointmentId ? nextRow : row,
    );

    if (nextRow.linkId) {
      this.baselineAppointmentRows = this.baselineAppointmentRows.map((row) =>
        row.linkId === nextRow.linkId
          ? this.cloneAppointmentRow(nextRow)
          : row,
      );
    }

    this.updateDirtyState();
    this.swalToastService.toastSuccess('Appointment updated successfully');
  }

  private async buildAppointmentEvent(
    row: LinkedAppointmentRow,
  ): Promise<EventInput | null> {
    const persistedAppointmentEvent = await this.fetchAppointmentEvent(
      row.appointmentId,
    );

    if (persistedAppointmentEvent) {
      return persistedAppointmentEvent;
    }

    if (row.appointmentEvent) {
      return this.normalizeAppointmentEventForEditor(row.appointmentEvent);
    }

    return this.createFallbackAppointmentEvent(row);
  }

  private async fetchAppointmentEvent(
    appointmentId: number,
  ): Promise<EventInput | null> {
    let appointment: IAppointment | undefined;

    try {
      const appointmentResponse = await firstValueFrom(
        this.appointmentService.getAppointmentsById(appointmentId.toString()),
      );
      appointment = appointmentResponse.data;
    } catch (error) {
      console.error('Unable to retrieve appointment details', error);
      return null;
    }

    if (!appointment) {
      return null;
    }

    const mappedEvents = mapBackendAppointmentDataToCalendarEvents([appointment]);
    const mappedEvent = mappedEvents[0];

    return mappedEvent
      ? this.normalizeAppointmentEventForEditor(mappedEvent)
      : null;
  }

  private createFallbackAppointmentEvent(row: LinkedAppointmentRow): EventInput {
    return {
      id: row.appointmentId.toString(),
      title: row.description !== '-' ? row.description : 'Appointment',
      start: row.nextSlotStartRaw ? new Date(row.nextSlotStartRaw) : undefined,
      end: row.lastSlotEndRaw ? new Date(row.lastSlotEndRaw) : undefined,
      resourceIds: [this.patientService.patientIDsignal()],
      extendedProps: {
        description: row.description !== '-' ? row.description : '',
        personnel: [],
        patients: [this.getCurrentPatientResource()],
        department: [],
        appointmentStatusId: row.appointmentStatusId ?? 3,
        appointmentServiceTypeId: row.appointmentServiceTypeId ?? null,
        appointmentReasonId: row.appointmentReasonId ?? null,
        slotId: 0,
        recurrentTypeId: 1,
        recurrenceOccurrences: null,
        personnelStatus: 30,
        patientStatus: 30,
        departmentStatus: 30,
      },
    };
  }

  private normalizeAppointmentEventForEditor(event: EventInput): EventInput {
    const patients = event.extendedProps?.['patients'] as
      | Array<{ id: number | string }>
      | undefined;

    return {
      ...event,
      id: event.id?.toString(),
      start: event.start ? new Date(event.start as string | number | Date) : undefined,
      end: event.end ? new Date(event.end as string | number | Date) : undefined,
      resourceIds:
        event.resourceIds ??
        patients?.map((patient) => patient.id.toString()) ??
        [this.patientService.patientIDsignal()],
    };
  }

  private async createStopMedicationAndStage(row: AvailableMedicationRow) {
    try {
      const response = await firstValueFrom(
        this.currentMedicationService.stopCurrentMedication(
          row.medicationId.toString(),
        ),
      );

      const stopMedicationId = response.data;

      if (!stopMedicationId) {
        throw new Error('Unable to resolve the new stop-medication record');
      }

      this.createdDraftStopMedicationIds.add(stopMedicationId);

      const stopMedications = await this.fetchPastMedications();
      const createdStopMedication = this.mapAvailableMedications(
        stopMedications.filter((medication) => medication.id === stopMedicationId),
      )[0];

      const stopBoundaryError = this.getMedicationBoundaryValidationMessage(
        createdStopMedication?.endDateTime ?? null,
      );

      if (stopBoundaryError) {
        await this.cleanupDraftMedication({
          uiId: this.createUiId(`draft-medication-${stopMedicationId}`),
          medicationId: stopMedicationId,
          sourceMedicationId: row.medicationId,
          activityState: 'Performed',
          actionTypeRaw: 'stop',
          medicationActionType: 'Stop',
          medication: createdStopMedication?.medication ?? row.medication,
          timing: createdStopMedication?.timing ?? row.timing,
          assignedDoctor:
            createdStopMedication?.assignedDoctor ?? row.assignedDoctor,
          onSetDateTime: createdStopMedication?.onSetDateTime ?? null,
          endDateTime: createdStopMedication?.endDateTime ?? null,
          deleteOnCancel: true,
        });
        this.swalToastService.toastError(stopBoundaryError);
        return;
      }

      const stagedRow = this.createMedicationRowFromAvailable(
        createdStopMedication ?? {
          medicationId: stopMedicationId,
          medication: row.medication,
          timing: row.timing,
          assignedDoctor: row.assignedDoctor,
          onSetDateTime: null,
          endDateTime: null,
        },
        'stop',
        true,
        row.medicationId,
      );

      this.linkedMedicationRows = [...this.linkedMedicationRows, stagedRow];
      this.closeMedicationLinkModal();
      this.loadAvailableMedications();
      this.updateDirtyState();
      this.swalToastService.toastSuccess(
        'Stop medication added to the care plan',
      );
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(error, 'Unable to create stop medication'),
      );
    }
  }

  private async stageCreatedServiceRequest(serviceRequestId: number) {
    this.createdDraftServiceRequestIds.add(serviceRequestId);

    try {
      const patientId = this.patientService.patientIDsignal();
      const visitId = this.eocService.currentVisitId() || undefined;
      const response = await firstValueFrom(
        this.serviceRequestService.getServiceRequests(patientId, visitId),
      );
      const createdServiceRequest = this.mapAvailableServiceRequests(
        (response.data ?? []).filter(
          (serviceRequest) => serviceRequest.id === serviceRequestId,
        ),
      )[0];

      const stagedRow = createdServiceRequest
        ? this.createServiceRequestRowFromAvailable(
            createdServiceRequest,
            true,
            'Planned',
          )
        : {
            uiId: this.createUiId(`draft-service-request-${serviceRequestId}`),
            serviceRequestId,
            activityState: 'Planned',
            status: '-',
            intent: '-',
            priority: '-',
            category: '-',
            procedure: '-',
            occurrence: '-',
            description: '-',
            deleteOnCancel: true,
          };

      this.linkedServiceRequestRows = [
        ...this.linkedServiceRequestRows,
        stagedRow,
      ];
      this.updateDirtyState();
      this.swalToastService.toastSuccess(
        'Service request created and added to the care plan',
      );
    } catch (error) {
      this.swalToastService.toastError(
        this.extractErrorMessage(
          error,
          'Service request was created, but it could not be staged in the care plan',
        ),
      );
    } finally {
      if (this.isServiceRequestLinkVisible) {
        this.loadAvailableServiceRequests();
      }
    }
  }

  private async applyActivityChanges(
    carePlanId: number,
    rollbackContext: SaveRollbackContext,
  ) {
    const appointmentDeletions = this.getPendingAppointmentDeletions();
    const medicationDeletions = this.getPendingMedicationDeletions();
    const serviceRequestDeletions = this.getPendingServiceRequestDeletions();
    const appointmentAdditions = this.getPendingAppointmentAdditions();
    const medicationAdditions = this.getPendingMedicationAdditions();
    const serviceRequestAdditions = this.getPendingServiceRequestAdditions();

    for (const row of appointmentDeletions) {
      rollbackContext.deletedAppointmentRows.push(
        this.cloneAppointmentRow(row),
      );
      await firstValueFrom(this.carePlanService.deleteActivity(row.linkId!));
    }

    for (const row of medicationDeletions) {
      rollbackContext.deletedMedicationRows.push(this.cloneMedicationRow(row));
      await firstValueFrom(this.carePlanService.deleteActivity(row.linkId!));
    }

    for (const row of serviceRequestDeletions) {
      rollbackContext.deletedServiceRequestRows.push(
        this.cloneServiceRequestRow(row),
      );
      await firstValueFrom(this.carePlanService.deleteActivity(row.linkId!));
    }

    for (const row of appointmentAdditions) {
      await firstValueFrom(
        this.carePlanService.linkAppointmentActivity(carePlanId, {
          appointmentId: row.appointmentId,
        }),
      );
      rollbackContext.addedAppointmentIds.push(row.appointmentId);
    }

    for (const row of medicationAdditions) {
      await firstValueFrom(
        this.carePlanService.linkMedicationActivity(carePlanId, {
          medicationId: row.medicationId,
          actionType: row.actionTypeRaw,
        }),
      );
      rollbackContext.addedMedicationRows.push(this.cloneMedicationRow(row));
    }

    for (const row of serviceRequestAdditions) {
      await firstValueFrom(
        this.carePlanService.linkServiceRequestActivity(carePlanId, {
          serviceRequestId: row.serviceRequestId,
        }),
      );
      rollbackContext.addedServiceRequestIds.push(row.serviceRequestId);
    }
  }

  private async applyAttachmentUploads(
    carePlanId: number,
    rollbackContext: SaveRollbackContext,
  ) {
    const pendingAttachments = this.getPendingAttachmentUploads();

    if (pendingAttachments.length === 0) {
      return;
    }

    await firstValueFrom(this.carePlanService.getCarePlanAttachments(carePlanId));

    for (const row of pendingAttachments) {
      const response = await firstValueFrom(
        this.carePlanService.uploadCarePlanAttachment(
          carePlanId,
          row.file!,
          row.date,
          row.description,
        ),
      );

      if (response.data?.id) {
        rollbackContext.uploadedAttachmentIds.push(response.data.id);
      }
    }
  }

  private async rollbackFailedSave(rollbackContext: SaveRollbackContext) {
    if (rollbackContext.createdCarePlanId) {
      await this.syncCreatedDraftStateAfterFailure(rollbackContext.createdCarePlanId);
      return;
    }

    if (!this.editCarePlanId) {
      return;
    }

    try {
      if (
        rollbackContext.addedAppointmentIds.length > 0 ||
        rollbackContext.addedMedicationRows.length > 0 ||
        rollbackContext.addedServiceRequestIds.length > 0
      ) {
        const activitiesResponse = await firstValueFrom(
          this.carePlanService.getActivities(this.editCarePlanId),
        );

        const currentActivities = activitiesResponse.data;
        const appointmentLinks = [
          ...currentActivities.plannedAppointments,
          ...currentActivities.performedAppointments,
        ];
        const medicationLinks = [
          ...currentActivities.plannedMedications,
          ...currentActivities.performedMedications,
        ];
        const serviceRequestLinks = [
          ...currentActivities.plannedServiceRequests,
          ...currentActivities.performedServiceRequests,
        ];

        for (const appointmentId of rollbackContext.addedAppointmentIds) {
          const createdLink = appointmentLinks.find(
            (item) => item.appointmentId === appointmentId,
          );

          if (createdLink?.linkId) {
            await firstValueFrom(
              this.carePlanService.deleteActivity(createdLink.linkId),
            );
          }
        }

        for (const medicationRow of rollbackContext.addedMedicationRows) {
          const createdLink = medicationLinks.find(
            (item) =>
              item.medicationId === medicationRow.medicationId &&
              this.normalizeMedicationActionType(item.medicationActionType) ===
                medicationRow.actionTypeRaw,
          );

          if (createdLink?.linkId) {
            await firstValueFrom(
              this.carePlanService.deleteActivity(createdLink.linkId),
            );
          }
        }

        for (const serviceRequestId of rollbackContext.addedServiceRequestIds) {
          const createdLink = serviceRequestLinks.find(
            (item) => item.serviceRequestId === serviceRequestId,
          );

          if (createdLink?.linkId) {
            await firstValueFrom(
              this.carePlanService.deleteActivity(createdLink.linkId),
            );
          }
        }
      }

      for (const row of rollbackContext.deletedAppointmentRows) {
        await firstValueFrom(
          this.carePlanService.linkAppointmentActivity(this.editCarePlanId, {
            appointmentId: row.appointmentId,
          }),
        );
      }

      for (const row of rollbackContext.deletedMedicationRows) {
        await firstValueFrom(
          this.carePlanService.linkMedicationActivity(this.editCarePlanId, {
            medicationId: row.medicationId,
            actionType: row.actionTypeRaw,
          }),
        );
      }

      for (const row of rollbackContext.deletedServiceRequestRows) {
        await firstValueFrom(
          this.carePlanService.linkServiceRequestActivity(this.editCarePlanId, {
            serviceRequestId: row.serviceRequestId,
          }),
        );
      }

      if (rollbackContext.summaryUpdated && this.loadedCarePlanSnapshot) {
        await firstValueFrom(
          this.carePlanService.editCarePlan({
            id: this.loadedCarePlanSnapshot.id,
            ...this.buildRequestFromDetailed(this.loadedCarePlanSnapshot),
          }),
        );
        this.restoreLocalSnapshot();
      }
    } catch (error) {
      console.error('Unable to roll back care plan changes', error);
    }
  }

  private async finishSuccessfulSave(
    carePlanId: number,
    message: string,
  ) {
    await this.refreshCarePlanViews(carePlanId);
    this.createdDraftCarePlanId = undefined;
    this.isSubmittedRevisionDraft = false;
    this.clearDraftResourceTracking();
    this.captureAttachmentBaseline();
    this.carePlanService.carePlanDirtySub.next(false);
    this.carePlanService.notifyCarePlanChanged();
    this.closeEditor();
    this.swalToastService.toastSuccess(message);
  }

  private showSaveError(error: unknown, rollbackContext: SaveRollbackContext) {
    const details = this.extractErrorMessage(error, 'Unable to save care plan');
    const keptDraftOpen = !!rollbackContext.createdCarePlanId;
    const reverted = rollbackContext.summaryUpdated && !keptDraftOpen;

    if (reverted && this.loadedCarePlanSnapshot) {
      this.restoreLocalSnapshot();
    }

    void Swal.fire({
      icon: 'error',
      title: 'Unable to save care plan',
      text: keptDraftOpen
        ? `${details} The draft care plan was kept open so you can retry or cancel it.`
        : reverted
          ? `${details} Changes were reverted.`
          : details,
    });
  }

  private buildCarePlanRequest(): ICarePlanRequest {
    const period = this.carePlanForm.value.period as Date[] | null;

    return {
      statusId: this.toNumber(this.carePlanForm.value.statusId) ?? undefined,
      intentId: this.toNumber(this.carePlanForm.value.intentId) ?? undefined,
      categoryId: this.toNumber(this.carePlanForm.value.categoryId),
      title: this.normalizeOptionalText(this.carePlanForm.value.title),
      description: this.normalizeOptionalText(
        this.carePlanForm.value.description,
      ),
      notes: this.normalizeOptionalText(this.carePlanForm.value.notes),
      periodStart: period?.[0] ? new Date(period[0]).toISOString() : null,
      periodEnd: period?.[1] ? new Date(period[1]).toISOString() : null,
      patientId:
        this.toNumber(this.patientService.patientIDsignal()) ?? undefined,
      visitId: this.isEdit
        ? undefined
        : (this.toNumber(this.eocService.currentVisitId()) ?? undefined),
    };
  }

  private buildRequestFromDetailed(data: ICarePlanDetailed): ICarePlanRequest {
    return {
      statusId: data.statusId,
      intentId: data.intentId,
      categoryId: data.categoryId,
      title: data.title ?? null,
      description: data.description ?? null,
      notes: data.notes ?? null,
      periodStart: data.periodStart ?? null,
      periodEnd: data.periodEnd ?? null,
      patientId: data.patientId,
    };
  }

  private buildCarePlanUpdatePatch(
    currentRequest: ICarePlanRequest,
  ): ICarePlanRequest {
    const current = this.normalizeRequest(currentRequest);
    const baseline = this.normalizeRequest(this.baselineRequestSnapshot);
    const patch: ICarePlanRequest = {};

    if (current.statusId !== baseline.statusId) {
      patch.statusId = currentRequest.statusId;
    }

    if (current.intentId !== baseline.intentId) {
      patch.intentId = currentRequest.intentId;
    }

    if (current.categoryId !== baseline.categoryId) {
      patch.categoryId = currentRequest.categoryId ?? null;
    }

    if (current.title !== baseline.title) {
      patch.title = currentRequest.title ?? null;
    }

    if (current.description !== baseline.description) {
      patch.description = currentRequest.description ?? null;
    }

    if (current.notes !== baseline.notes) {
      patch.notes = currentRequest.notes ?? null;
    }

    if (current.periodStart !== baseline.periodStart) {
      patch.periodStart = currentRequest.periodStart ?? null;
    }

    if (current.periodEnd !== baseline.periodEnd) {
      patch.periodEnd = currentRequest.periodEnd ?? null;
    }

    return patch;
  }

  private activateCreatedDraftCarePlan(
    carePlanId: number,
    request: ICarePlanRequest,
  ) {
    this.createdDraftCarePlanId = carePlanId;
    this.isEdit = true;
    this.editCarePlanId = carePlanId;
    this.baselineRequestSnapshot = { ...request };
    this.captureActivityBaseline();
    this.captureAttachmentBaseline();
    this.updateDirtyState();
  }

  private async activateSubmittedCarePlanRevision(
    carePlanId: number,
    request: ICarePlanRequest,
  ) {
    this.createdDraftCarePlanId = carePlanId;
    this.isSubmittedRevisionDraft = true;
    this.isEdit = true;
    this.editCarePlanId = carePlanId;
    this.baselineRequestSnapshot = { ...request };

    if (this.loadedCarePlanSnapshot) {
      const originalCarePlanId = this.loadedCarePlanSnapshot.id;
      this.loadedCarePlanSnapshot = {
        ...this.loadedCarePlanSnapshot,
        id: carePlanId,
        statusId: request.statusId ?? this.loadedCarePlanSnapshot.statusId,
        intentId: request.intentId ?? this.loadedCarePlanSnapshot.intentId,
        categoryId: request.categoryId ?? null,
        title: request.title ?? null,
        description: request.description ?? null,
        notes: request.notes ?? null,
        periodStart: request.periodStart ?? null,
        periodEnd: request.periodEnd ?? null,
        visitId:
          this.toNumber(this.eocService.currentVisitId()) ??
          this.loadedCarePlanSnapshot.visitId,
        isSubmitted: false,
        replacementId: originalCarePlanId,
      };
    }

    const activitiesResponse = await firstValueFrom(
      this.carePlanService.getActivities(carePlanId),
    );
    const draftAppointmentRows = [
      ...activitiesResponse.data.plannedAppointments,
      ...activitiesResponse.data.performedAppointments,
    ].map((item) => this.mapAppointmentActivityRow(item));
    const draftMedicationRows = [
      ...activitiesResponse.data.plannedMedications,
      ...activitiesResponse.data.performedMedications,
    ].map((item) => this.mapMedicationActivityRow(item));
    const draftServiceRequestRows = [
      ...activitiesResponse.data.plannedServiceRequests,
      ...activitiesResponse.data.performedServiceRequests,
    ].map((item) => this.mapServiceRequestActivityRow(item));

    this.linkedAppointmentRows = this.linkedAppointmentRows.map((row) => ({
      ...row,
      linkId: draftAppointmentRows.find(
        (draftRow) => draftRow.appointmentId === row.appointmentId,
      )?.linkId,
    }));
    this.linkedMedicationRows = this.linkedMedicationRows.map((row) => ({
      ...row,
      linkId: draftMedicationRows.find(
        (draftRow) =>
          draftRow.medicationId === row.medicationId &&
          draftRow.actionTypeRaw === row.actionTypeRaw,
      )?.linkId,
    }));
    this.linkedServiceRequestRows = this.linkedServiceRequestRows.map((row) => ({
      ...row,
      linkId: draftServiceRequestRows.find(
        (draftRow) =>
          draftRow.serviceRequestId === row.serviceRequestId,
      )?.linkId,
    }));
    this.baselineAppointmentRows = draftAppointmentRows;
    this.baselineMedicationRows = draftMedicationRows;
    this.baselineServiceRequestRows = draftServiceRequestRows;
    this.updateDirtyState();
  }

  private async resolveCreatedCarePlanId(
    request: ICarePlanRequest,
  ): Promise<number | undefined> {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId();
    const response = await firstValueFrom(
      this.carePlanService.getCarePlan(patientId, visitId),
    );

    return [...(response.data ?? [])]
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      )
      .find((carePlan) => this.matchesCreatedCarePlan(carePlan, request))?.id;
  }

  private matchesCreatedCarePlan(
    carePlan: ICarePlanDetailed,
    request: ICarePlanRequest,
  ): boolean {
    return (
      carePlan.patientId === request.patientId &&
      carePlan.visitId === request.visitId &&
      carePlan.statusId === request.statusId &&
      carePlan.intentId === request.intentId &&
      (carePlan.title ?? null) === (request.title ?? null) &&
      (carePlan.description ?? null) === (request.description ?? null)
    );
  }

  private async syncCreatedDraftStateAfterFailure(carePlanId: number) {
    try {
      const [activitiesResponse, attachmentsResponse] = await Promise.all([
        firstValueFrom(this.carePlanService.getActivities(carePlanId)),
        firstValueFrom(this.carePlanService.getCarePlanAttachments(carePlanId)),
      ]);

      const persistedAppointmentRows = [
        ...activitiesResponse.data.plannedAppointments.map((item) =>
          this.mapAppointmentActivityRow(item),
        ),
        ...activitiesResponse.data.performedAppointments.map((item) =>
          this.mapAppointmentActivityRow(item),
        ),
      ];
      const persistedMedicationRows = [
        ...activitiesResponse.data.plannedMedications.map((item) =>
          this.mapMedicationActivityRow(item),
        ),
        ...activitiesResponse.data.performedMedications.map((item) =>
          this.mapMedicationActivityRow(item),
        ),
      ];
      const persistedServiceRequestRows = [
        ...activitiesResponse.data.plannedServiceRequests.map((item) =>
          this.mapServiceRequestActivityRow(item),
        ),
        ...activitiesResponse.data.performedServiceRequests.map((item) =>
          this.mapServiceRequestActivityRow(item),
        ),
      ];
      const persistedAttachmentRows = (attachmentsResponse.data ?? []).map(
        (row) => this.mapAttachmentRow(row),
      );

      const pendingAppointmentRows = this.linkedAppointmentRows
        .filter(
          (row) =>
            !persistedAppointmentRows.some(
              (persistedRow) =>
                persistedRow.appointmentId === row.appointmentId &&
                persistedRow.linkId,
            ),
        )
        .map((row) => this.cloneAppointmentRow(row));
      const pendingMedicationRows = this.linkedMedicationRows
        .filter(
          (row) =>
            !persistedMedicationRows.some(
              (persistedRow) =>
                persistedRow.medicationId === row.medicationId &&
                persistedRow.actionTypeRaw === row.actionTypeRaw &&
                persistedRow.linkId,
            ),
        )
        .map((row) => this.cloneMedicationRow(row));
      const pendingServiceRequestRows = this.linkedServiceRequestRows
        .filter(
          (row) =>
            !persistedServiceRequestRows.some(
              (persistedRow) =>
                persistedRow.serviceRequestId === row.serviceRequestId &&
                persistedRow.linkId,
            ),
        )
        .map((row) => this.cloneServiceRequestRow(row));
      const pendingAttachmentRows = this.attachmentRows
        .filter(
          (row) =>
            row.isPendingUpload &&
            !persistedAttachmentRows.some(
              (persistedRow) =>
                persistedRow.fileName === row.fileName &&
                (persistedRow.date ?? null) === (row.date ?? null) &&
                (persistedRow.description ?? null) ===
                  (row.description ?? null) &&
                (persistedRow.size ?? null) === (row.size ?? null),
            ),
        )
        .map((row) => this.cloneAttachmentRow(row));

      this.linkedAppointmentRows = [
        ...persistedAppointmentRows,
        ...pendingAppointmentRows,
      ];
      this.linkedMedicationRows = [
        ...persistedMedicationRows,
        ...pendingMedicationRows,
      ];
      this.linkedServiceRequestRows = [
        ...persistedServiceRequestRows,
        ...pendingServiceRequestRows,
      ];
      this.attachmentRows = [
        ...persistedAttachmentRows,
        ...pendingAttachmentRows,
      ];

      this.baselineAppointmentRows = persistedAppointmentRows.map((row) =>
        this.cloneAppointmentRow(row),
      );
      this.baselineMedicationRows = persistedMedicationRows.map((row) =>
        this.cloneMedicationRow(row),
      );
      this.baselineServiceRequestRows = persistedServiceRequestRows.map((row) =>
        this.cloneServiceRequestRow(row),
      );
      this.baselineAttachmentRows = persistedAttachmentRows.map((row) =>
        this.cloneAttachmentRow(row),
      );
      this.updateDirtyState();
    } catch (error) {
      console.error('Unable to refresh created care plan draft state', error);
    }
  }

  private async refreshCarePlanViews(carePlanId?: number) {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId() || undefined;
    const dropdownData =
      this.dropdownData ??
      (await firstValueFrom(this.carePlanService.getDropdownData())).data;

    const summaryResponse = await firstValueFrom(
      this.carePlanService.getCarePlan(patientId, visitId),
    );
    const activeResponse = await firstValueFrom(
      this.carePlanService.getActiveCarePlans(patientId, visitId),
    );
    const pastResponse = await firstValueFrom(
      this.carePlanService.getPastCarePlans(patientId, visitId),
    );

    this.carePlanService.carePlanDataSub.next(
      this.carePlanService.enrichTerminologyDisplays(
        summaryResponse.data ?? [],
        dropdownData,
      ),
    );
    this.carePlanService.activeCarePlanDataSub.next(
      this.carePlanService.enrichTerminologyDisplays(
        activeResponse.data ?? [],
        dropdownData,
      ),
    );
    this.carePlanService.pastCarePlanDataSub.next(
      this.carePlanService.enrichTerminologyDisplays(
        pastResponse.data ?? [],
        dropdownData,
      ),
    );

    if (carePlanId) {
      const activitiesResponse = await firstValueFrom(
        this.carePlanService.getActivities(carePlanId),
      );
      this.carePlanService.carePlanActivitiesSub.next(activitiesResponse.data);
      await firstValueFrom(this.carePlanService.getCarePlanAttachments(carePlanId));
    } else {
      this.carePlanService.carePlanActivitiesSub.next(null);
    }

    this.carePlanService.carePlanLoadingSub.next(false);
  }

  private normalizeAttachmentDate(value: unknown): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    return new Date(value as Date).toISOString().split('T')[0];
  }

  private restoreLocalSnapshot() {
    this.suppressDirtyTracking = true;

    if (!this.loadedCarePlanSnapshot) {
      this.carePlanForm.reset({
        statusId: this.baselineRequestSnapshot.statusId ?? null,
        intentId: this.baselineRequestSnapshot.intentId ?? null,
        categoryId: this.baselineRequestSnapshot.categoryId ?? null,
        title: this.baselineRequestSnapshot.title ?? null,
        description: this.baselineRequestSnapshot.description ?? null,
        notes: this.baselineRequestSnapshot.notes ?? null,
        period:
          this.baselineRequestSnapshot.periodStart ||
          this.baselineRequestSnapshot.periodEnd
            ? [
                this.toDate(this.baselineRequestSnapshot.periodStart),
                this.toDate(this.baselineRequestSnapshot.periodEnd),
              ]
            : null,
      });
    } else {
      this.carePlanForm.reset({
        statusId: this.baselineRequestSnapshot.statusId ?? null,
        intentId: this.baselineRequestSnapshot.intentId ?? null,
        categoryId: this.baselineRequestSnapshot.categoryId ?? null,
        title: this.baselineRequestSnapshot.title ?? null,
        description: this.baselineRequestSnapshot.description ?? null,
        notes: this.baselineRequestSnapshot.notes ?? null,
        period:
          this.baselineRequestSnapshot.periodStart ||
          this.baselineRequestSnapshot.periodEnd
            ? [
                this.toDate(this.baselineRequestSnapshot.periodStart),
                this.toDate(this.baselineRequestSnapshot.periodEnd),
              ]
            : null,
      });
    }

    this.linkedAppointmentRows = this.baselineAppointmentRows.map((row) =>
      this.cloneAppointmentRow(row),
    );
    this.linkedMedicationRows = this.baselineMedicationRows.map((row) =>
      this.cloneMedicationRow(row),
    );
    this.linkedServiceRequestRows = this.baselineServiceRequestRows.map((row) =>
      this.cloneServiceRequestRow(row),
    );
    this.attachmentRows = this.baselineAttachmentRows.map((row) =>
      this.cloneAttachmentRow(row),
    );
    this.suppressDirtyTracking = false;
    this.updateDirtyState();
  }

  private updateDirtyState() {
    if (this.suppressDirtyTracking || this.isReadOnly) {
      this.carePlanService.carePlanDirtySub.next(false);
      return;
    }

    const formChanged =
      JSON.stringify(this.normalizeRequest(this.buildCarePlanRequest())) !==
      JSON.stringify(this.normalizeRequest(this.baselineRequestSnapshot));

    const appointmentsChanged =
      JSON.stringify(
        this.serializeAppointmentRows(this.linkedAppointmentRows),
      ) !==
      JSON.stringify(
        this.serializeAppointmentRows(this.baselineAppointmentRows),
      );

    const medicationsChanged =
      JSON.stringify(
        this.serializeMedicationRows(this.linkedMedicationRows),
      ) !==
      JSON.stringify(this.serializeMedicationRows(this.baselineMedicationRows));

    const serviceRequestsChanged =
      JSON.stringify(
        this.serializeServiceRequestRows(this.linkedServiceRequestRows),
      ) !==
      JSON.stringify(
        this.serializeServiceRequestRows(this.baselineServiceRequestRows),
      );

    const attachmentsChanged =
      JSON.stringify(this.serializeAttachmentRows(this.attachmentRows)) !==
      JSON.stringify(
        this.serializeAttachmentRows(this.baselineAttachmentRows),
      );

    this.carePlanService.carePlanDirtySub.next(
      formChanged ||
        appointmentsChanged ||
        medicationsChanged ||
        serviceRequestsChanged ||
        attachmentsChanged,
    );
  }

  private normalizeRequest(request: ICarePlanRequest) {
    return {
      statusId: request.statusId ?? null,
      intentId: request.intentId ?? null,
      categoryId: request.categoryId ?? null,
      title: request.title ?? null,
      description: request.description ?? null,
      notes: request.notes ?? null,
      periodStart: request.periodStart ?? null,
      periodEnd: request.periodEnd ?? null,
      patientId: request.patientId ?? null,
      visitId: request.visitId ?? null,
    };
  }

  private serializeAppointmentRows(rows: LinkedAppointmentRow[]) {
    return rows
      .map((row) => ({
        linkId: row.linkId ?? null,
        appointmentId: row.appointmentId,
        activityState: row.activityState,
      }))
      .sort((a, b) =>
        `${a.linkId ?? 'z'}-${a.appointmentId}`.localeCompare(
          `${b.linkId ?? 'z'}-${b.appointmentId}`,
        ),
      );
  }

  private serializeMedicationRows(rows: LinkedMedicationRow[]) {
    return rows
      .map((row) => ({
        linkId: row.linkId ?? null,
        medicationId: row.medicationId,
        sourceMedicationId: row.sourceMedicationId,
        actionTypeRaw: row.actionTypeRaw,
      }))
      .sort((a, b) =>
        `${a.linkId ?? 'z'}-${a.medicationId}-${a.actionTypeRaw}`.localeCompare(
          `${b.linkId ?? 'z'}-${b.medicationId}-${b.actionTypeRaw}`,
        ),
      );
  }

  private serializeServiceRequestRows(rows: LinkedServiceRequestRow[]) {
    return rows
      .map((row) => ({
        linkId: row.linkId ?? null,
        serviceRequestId: row.serviceRequestId,
        activityState: row.activityState,
      }))
      .sort((a, b) =>
        `${a.linkId ?? 'z'}-${a.serviceRequestId}`.localeCompare(
          `${b.linkId ?? 'z'}-${b.serviceRequestId}`,
        ),
      );
  }

  private serializeAttachmentRows(rows: CarePlanAttachmentRow[]) {
    return rows
      .map((row) => ({
        id: row.id ?? null,
        fileName: row.fileName,
        date: row.date ?? null,
        description: row.description ?? null,
        size: row.size ?? null,
        isPendingUpload: row.isPendingUpload,
      }))
      .sort((a, b) =>
        `${a.id ?? 'z'}-${a.fileName}-${a.date ?? ''}`.localeCompare(
          `${b.id ?? 'z'}-${b.fileName}-${b.date ?? ''}`,
        ),
      );
  }

  private captureActivityBaseline() {
    this.baselineAppointmentRows = this.linkedAppointmentRows.map((row) =>
      this.cloneAppointmentRow(row),
    );
    this.baselineMedicationRows = this.linkedMedicationRows.map((row) =>
      this.cloneMedicationRow(row),
    );
    this.baselineServiceRequestRows = this.linkedServiceRequestRows.map((row) =>
      this.cloneServiceRequestRow(row),
    );
  }

  private captureAttachmentBaseline() {
    this.baselineAttachmentRows = this.attachmentRows.map((row) =>
      this.cloneAttachmentRow(row),
    );
  }

  private getPendingAppointmentAdditions(): LinkedAppointmentRow[] {
    return this.linkedAppointmentRows.filter((row) => !row.linkId);
  }

  private getPendingMedicationAdditions(): LinkedMedicationRow[] {
    return this.linkedMedicationRows.filter((row) => !row.linkId);
  }

  private getPendingServiceRequestAdditions(): LinkedServiceRequestRow[] {
    return this.linkedServiceRequestRows.filter((row) => !row.linkId);
  }

  private getPendingAttachmentUploads(): CarePlanAttachmentRow[] {
    return this.attachmentRows.filter(
      (row) => row.isPendingUpload && !!row.file,
    );
  }

  private getPendingAppointmentDeletions(): LinkedAppointmentRow[] {
    const currentLinkIds = new Set(
      this.linkedAppointmentRows
        .filter((row) => row.linkId)
        .map((row) => row.linkId!),
    );

    return this.baselineAppointmentRows.filter(
      (row) => row.linkId && !currentLinkIds.has(row.linkId),
    );
  }

  private getPendingMedicationDeletions(): LinkedMedicationRow[] {
    const currentLinkIds = new Set(
      this.linkedMedicationRows
        .filter((row) => row.linkId)
        .map((row) => row.linkId!),
    );

    return this.baselineMedicationRows.filter(
      (row) => row.linkId && !currentLinkIds.has(row.linkId),
    );
  }

  private getPendingServiceRequestDeletions(): LinkedServiceRequestRow[] {
    const currentLinkIds = new Set(
      this.linkedServiceRequestRows
        .filter((row) => row.linkId)
        .map((row) => row.linkId!),
    );

    return this.baselineServiceRequestRows.filter(
      (row) => row.linkId && !currentLinkIds.has(row.linkId),
    );
  }

  private applyCreateDefaults() {
    if (!this.dropdownData) {
      return;
    }

    const activeStatus = this.dropdownData.statuses.find(
      (status) => status.code.toLowerCase() === 'active',
    );
    const planIntent = this.dropdownData.intents.find(
      (intent) => intent.code.toLowerCase() === 'plan',
    );

    this.carePlanForm.patchValue({
      statusId: this.carePlanForm.value.statusId ?? activeStatus?.id ?? null,
      intentId: this.carePlanForm.value.intentId ?? planIntent?.id ?? null,
    });
  }

  private resetActivities() {
    this.linkedAppointmentRows = [];
    this.availableAppointmentRows = [];
    this.linkedMedicationRows = [];
    this.availableMedicationRows = [];
    this.linkedServiceRequestRows = [];
    this.availableServiceRequestRows = [];
  }

  private resetAttachments() {
    this.revokeAttachmentPreviewUrl();
    this.attachmentRows
      .filter((row) => !!row.objectUrl)
      .forEach((row) => URL.revokeObjectURL(row.objectUrl!));
    this.attachmentRows = [];
    this.isAttachmentsLoading = false;
    this.closeAttachmentUploadModal();
  }

  private syncReadOnlyState() {
    if (this.isReadOnly) {
      this.carePlanForm.disable({ emitEvent: false });
      return;
    }

    this.carePlanForm.enable({ emitEvent: false });
  }

  private closeEditor() {
    this.closeAppointmentLinkModal();
    this.closeMedicationLinkModal();
    this.closeMedicationAddModal();
    this.closeServiceRequestLinkModal();
    this.closeServiceRequestAddModal();
    this.closeAttachmentUploadModal();
    this.revokeAttachmentPreviewUrl();
    this.isPdfPreviewVisible = false;
    this.pdfPreviewFileName = undefined;
    this.carePlanService.resetEditorState();
  }

  private getAppointmentScope(): 'mine' | 'team' {
    return this.appointmentService.hasPermission(['a-app']) ? 'team' : 'mine';
  }

  private getCurrentPatientResource() {
    const patientId = this.patientService.patientIDsignal();
    const dropdownPatient = this.appointmentDropdownData?.patients?.find(
      (patient) => patient.id.toString() === patientId,
    );

    return {
      id: patientId,
      title: dropdownPatient?.fullName || this.patientContextLabel,
    };
  }

  private getAppointmentPersonnelResources() {
    return (this.appointmentDropdownData?.users ?? []).map((user) => ({
      id: user.id,
      title: user.fullName,
    }));
  }

  private getAppointmentStatusLabel(statusId?: number | null): string {
    const appointmentStatuses = this.getAppointmentStatusesByType(1);

    return (
      appointmentStatuses.find(
        (status) => status.id === statusId,
      )?.name ?? (statusId ? String(statusId) : '-')
    );
  }

  private getAppointmentStatusesByType(typeId: number): IAppointmentStatus[] {
    if (typeId === 1 && this.appointmentStatuses.length > 0) {
      return this.appointmentStatuses;
    }

    return (this.appointmentDropdownData?.statuses ?? []).filter(
      (status) => status.typeId === typeId,
    );
  }

  private getAppointmentServiceTypeLabel(
    serviceTypeId?: number | null,
  ): string {
    return (
      this.appointmentDropdownData?.serviceTypes?.find(
        (serviceType) => serviceType.id === serviceTypeId,
      )?.name ?? '-'
    );
  }

  private getAppointmentReasonLabel(reasonId?: number | null): string {
    return (
      this.appointmentDropdownData?.reasons?.find(
        (reason) => reason.id === reasonId,
      )?.name ?? '-'
    );
  }

  private formatAppointmentRange(start?: string, end?: string): string {
    if (!start) {
      return '-';
    }

    const startDate = new Date(start);
    const endDate = end ? new Date(end) : undefined;
    const datePart = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(startDate);
    const startTime = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(startDate);

    if (!endDate) {
      return `${datePart} ${startTime}`;
    }

    const endTime = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(endDate);

    return `${datePart} ${startTime} - ${endTime}`;
  }

  private toEventDateIso(value: EventInput['start'] | EventInput['end']): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return new Date(value).toISOString();
    }

    return null;
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(value));
  }

  private formatMedicationTiming(
    start?: string | null,
    end?: string | null,
  ): string {
    const startLabel = this.formatDateTime(start);
    const endLabel = this.formatDateTime(end);

    if (startLabel === '-' && endLabel === '-') {
      return '-';
    }

    if (endLabel === '-') {
      return startLabel;
    }

    if (startLabel === '-') {
      return endLabel;
    }

    return `${startLabel} - ${endLabel}`;
  }

  private formatServiceRequestOccurrence(
    start?: string | null,
    end?: string | null,
  ): string {
    return this.formatMedicationTiming(start, end);
  }

  formatAttachmentDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const normalizedValue =
      value.includes('T') || value.includes(' ')
        ? value
        : `${value}T00:00:00`;

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(normalizedValue));
  }

  formatAttachmentSize(size?: number | null): string {
    if (size === null || size === undefined || Number.isNaN(size)) {
      return '-';
    }

    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private getSelectedPeriodStart(): Date | null {
    const period = this.carePlanForm.value.period as Date[] | null;
    return period?.[0] ? new Date(period[0]) : null;
  }

  private getCarePlanPeriodEndDate(): Date | null {
    const period = this.carePlanForm.value.period as Date[] | null;
    return period?.[1] ? new Date(period[1]) : null;
  }

  private getCarePlanPeriodEndIso(): string | null {
    const periodEnd = this.getCarePlanPeriodEndDate();
    return periodEnd ? periodEnd.toISOString() : null;
  }

  private isPastStartDateBlocked(periodStart: Date | null): boolean {
    if (!periodStart) {
      return false;
    }

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const selectedStart = new Date(periodStart);
    selectedStart.setHours(0, 0, 0, 0);

    if (selectedStart >= todayStart) {
      return false;
    }

    const baselineStart = this.baselineRequestSnapshot.periodStart
      ? new Date(this.baselineRequestSnapshot.periodStart)
      : null;

    if (baselineStart) {
      baselineStart.setHours(0, 0, 0, 0);
    }

    return !baselineStart || baselineStart.getTime() !== selectedStart.getTime();
  }

  private getActivityBoundaryValidationMessage(): string | null {
    for (const row of this.linkedAppointmentRows) {
      const appointmentError = this.getAppointmentBoundaryValidationMessage(row);
      if (appointmentError) {
        return appointmentError;
      }
    }

    for (const row of this.linkedMedicationRows) {
      const medicationError = this.getMedicationBoundaryValidationMessage(
        row.endDateTime,
      );
      if (medicationError) {
        return medicationError;
      }
    }

    return null;
  }

  private getAppointmentBoundaryValidationMessage(
    row:
      | AvailableAppointmentRow
      | LinkedAppointmentRow
      | { lastSlotEndRaw?: string | null; nextSlotStartRaw?: string | null },
  ): string | null {
    const carePlanPeriodEnd = this.getCarePlanPeriodEndDate();
    if (!carePlanPeriodEnd) {
      return null;
    }

    const lastSlotEnd = row.lastSlotEndRaw
      ? new Date(row.lastSlotEndRaw)
      : row.nextSlotStartRaw
        ? new Date(row.nextSlotStartRaw)
        : null;

    if (!lastSlotEnd) {
      return null;
    }

    return lastSlotEnd > carePlanPeriodEnd
      ? this.translateMessage(
          'The care plan end date cannot be before one or more linked appointment slots.',
        )
      : null;
  }

  private getMedicationBoundaryValidationMessage(
    endDateTime?: string | null,
  ): string | null {
    const carePlanPeriodEnd = this.getCarePlanPeriodEndDate();
    if (!carePlanPeriodEnd) {
      return null;
    }

    if (!endDateTime) {
      return this.translateMessage(
        'Linked medications must have an end date before they can be added to a care plan.',
      );
    }

    return new Date(endDateTime) > carePlanPeriodEnd
      ? this.translateMessage(
          'The care plan end date cannot be before one or more linked medications.',
        )
      : null;
  }

  private deriveServiceRequestState(
    statusCode?: string | null,
    occurrenceEnd?: string | null,
  ): string {
    const normalizedStatus = statusCode?.toLowerCase();

    if (normalizedStatus === 'completed' || normalizedStatus === 'revoked') {
      return 'Performed';
    }

    if (occurrenceEnd && new Date(occurrenceEnd) < new Date()) {
      return 'Performed';
    }

    return 'Planned';
  }

  private normalizeOptionalText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return value === null ? null : null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private capitalize(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private normalizeMedicationActionType(
    value?: string | null,
  ): 'continue' | 'stop' {
    return value?.toLowerCase() === 'stop' ? 'stop' : 'continue';
  }

  private isCurrentUserCarePlanAuthor(carePlan?: ICarePlanDetailed): boolean {
    const currentUserId =
      this.keycloakService.getKeycloakInstance()?.tokenParsed?.['sub'];

    if (!currentUserId || !carePlan?.authorUserId) {
      return false;
    }

    return (
      String(currentUserId).toLowerCase() ===
      String(carePlan.authorUserId).toLowerCase()
    );
  }

  private mapAttachmentRow(
    row: ICarePlanAttachmentDetailed,
  ): CarePlanAttachmentRow {
    return {
      uiId: this.createUiId(`attachment-${row.id}`),
      id: row.id,
      carePlanId: row.carePlanId,
      date: row.date,
      description: row.description,
      fileName: row.fileName,
      contentType: row.contentType,
      size: row.size,
      createdDate: row.createdDate,
      createdByUserId: row.createdByUserId,
      isSubmitted: row.isSubmitted,
      downloadUrl: row.downloadUrl,
      isPendingUpload: false,
      objectUrl: null,
    };
  }

  private previewAttachmentObjectUrl(
    objectUrl: string,
    contentType: string | null | undefined,
    fileName: string,
    ownsUrl: boolean,
  ) {
    this.revokeAttachmentPreviewUrl();
    this.attachmentPreviewUrl = objectUrl;
    this.attachmentPreviewOwnsUrl = ownsUrl;

    if (this.isImageAttachment(contentType, fileName)) {
      const previewRef = this.imageService.preview([
        {
          src: objectUrl,
          alt: fileName,
        },
      ]);

      if (ownsUrl) {
        const originalClose = previewRef.close.bind(previewRef);
        previewRef.close = () => {
          originalClose();
          this.revokeAttachmentPreviewUrl();
        };
      }

      return;
    }

    if (this.isPdfAttachment(contentType, fileName)) {
      this.pdfPreviewFileName = fileName;
      this.isPdfPreviewVisible = true;
    }
  }

  canPreviewAttachment(row: CarePlanAttachmentRow): boolean {
    const fileName = row.fileName.toLowerCase();
    const contentType = row.contentType?.toLowerCase() ?? '';

    return (
      contentType.includes('pdf') ||
      contentType.startsWith('image/') ||
      /\.(pdf|png|jpe?g|webp|gif|bmp|svg)$/i.test(fileName)
    );
  }

  private revokeAttachmentPreviewUrl() {
    if (this.attachmentPreviewOwnsUrl && this.attachmentPreviewUrl) {
      URL.revokeObjectURL(this.attachmentPreviewUrl);
    }
    this.attachmentPreviewUrl = undefined;
    this.attachmentPreviewOwnsUrl = false;
  }

  private isImageAttachment(
    contentType: string | null | undefined,
    fileName: string,
  ): boolean {
    const normalizedContentType = contentType?.toLowerCase() ?? '';
    const normalizedFileName = fileName.toLowerCase();

    return (
      normalizedContentType.startsWith('image/') ||
      /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(normalizedFileName)
    );
  }

  private isPdfAttachment(
    contentType: string | null | undefined,
    fileName: string,
  ): boolean {
    const normalizedContentType = contentType?.toLowerCase() ?? '';
    const normalizedFileName = fileName.toLowerCase();

    return (
      normalizedContentType.includes('pdf') ||
      normalizedFileName.endsWith('.pdf')
    );
  }

  private mapAttachmentDownloadPayload(
    response: { body: Blob | null; headers: { get(name: string): string | null } },
    row: CarePlanAttachmentRow,
  ): AttachmentDownloadPayload {
    const contentDisposition = response.headers.get('content-disposition') ?? '';
    const fileNameMatch =
      /filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(
        contentDisposition,
      );
    const fileName = decodeURIComponent(
      fileNameMatch?.[1] || fileNameMatch?.[2] || row.fileName,
    );

    return {
      blob:
        response.body ??
        new Blob([], { type: row.contentType || 'application/octet-stream' }),
      fileName,
    };
  }

  private translateMessage(key: string): string {
    return this.translate.instant(key);
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private toDate(value?: string | null): Date | null {
    return value ? new Date(value) : null;
  }

  private createUiId(prefix: string): string {
    this.uiSequence += 1;
    return `${prefix}-${this.uiSequence}`;
  }

  private cloneAppointmentRow(row: LinkedAppointmentRow): LinkedAppointmentRow {
    return { ...row };
  }

  private cloneAttachmentRow(row: CarePlanAttachmentRow): CarePlanAttachmentRow {
    return { ...row };
  }

  private refreshAppointmentLabels() {
    this.linkedAppointmentRows = this.linkedAppointmentRows.map((row) => ({
      ...row,
      status: this.getAppointmentStatusLabel(row.appointmentStatusId),
      serviceType: this.getAppointmentServiceTypeLabel(
        row.appointmentServiceTypeId,
      ),
      reason: this.getAppointmentReasonLabel(row.appointmentReasonId),
    }));

    this.baselineAppointmentRows = this.baselineAppointmentRows.map((row) => ({
      ...row,
      status: this.getAppointmentStatusLabel(row.appointmentStatusId),
      serviceType: this.getAppointmentServiceTypeLabel(
        row.appointmentServiceTypeId,
      ),
      reason: this.getAppointmentReasonLabel(row.appointmentReasonId),
    }));

    this.availableAppointmentRows = this.availableAppointmentRows.map((row) => ({
      ...row,
      status: this.getAppointmentStatusLabel(row.appointmentStatusId),
      serviceType: this.getAppointmentServiceTypeLabel(
        row.appointmentServiceTypeId,
      ),
      reason: this.getAppointmentReasonLabel(row.appointmentReasonId),
    }));
  }

  private cloneMedicationRow(row: LinkedMedicationRow): LinkedMedicationRow {
    return { ...row };
  }

  private cloneServiceRequestRow(
    row: LinkedServiceRequestRow,
  ): LinkedServiceRequestRow {
    return { ...row };
  }

  private async fetchCurrentMedications(): Promise<IMedicationDetailed[]> {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId() || undefined;
    const response = await firstValueFrom(
      this.currentMedicationService.getCurrentMedication(patientId, visitId),
    );

    return response.data?.getAll ?? [];
  }

  private async fetchPastMedications(): Promise<IMedicationDetailed[]> {
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId() || undefined;
    const response = await firstValueFrom(
      this.pastMedicationService.getPastMedication(patientId, visitId),
    );

    return response.data ?? [];
  }

  private async resolveMedicationForView(
    row: LinkedMedicationRow,
  ): Promise<IMedicationDetailed | undefined> {
    const [currentMedications, pastMedications] = await Promise.all([
      this.fetchCurrentMedications().catch(() => []),
      this.fetchPastMedications().catch(() => []),
    ]);
    const medications = [...currentMedications, ...pastMedications];

    return (
      medications.find((medication) => medication.id === row.medicationId) ??
      medications.find((medication) => medication.id === row.sourceMedicationId)
    );
  }

  private async discardUnsavedDraftState(
    showErrors: boolean,
  ): Promise<boolean> {
    if (this.createdDraftCarePlanId) {
      const createdDraftCarePlanId = this.createdDraftCarePlanId;

      try {
        await firstValueFrom(
          this.carePlanService.deleteCarePlan(
            createdDraftCarePlanId,
            !this.isSubmittedRevisionDraft,
          ),
        );
        this.createdDraftCarePlanId = undefined;
        this.isSubmittedRevisionDraft = false;
        await this.refreshCarePlanViews();
      } catch (error) {
        if (showErrors) {
          await Swal.fire({
            icon: 'error',
            title: this.translateMessage('Unable to discard care plan draft'),
            text: this.extractErrorMessage(
              error,
              'Unable to discard care plan draft',
            ),
          });
        } else {
          console.error('Unable to discard care plan draft', error);
        }

        return false;
      }
    }

    await this.cleanupCreatedDraftResources();
    return true;
  }

  private async cleanupCreatedDraftResources() {
    const appointmentIds = [...this.createdDraftAppointmentIds];
    const currentMedicationIds = [...this.createdDraftCurrentMedicationIds];
    const stopMedicationIds = [...this.createdDraftStopMedicationIds];
    const serviceRequestIds = [...this.createdDraftServiceRequestIds];

    this.clearDraftResourceTracking();

    await Promise.all(
      appointmentIds.map(async (appointmentId) => {
        try {
          await firstValueFrom(
            this.appointmentService.deleteAppointment(appointmentId),
          );
        } catch (error) {
          console.error('Unable to clean up draft appointment', error);
        }
      }),
    );

    await Promise.all(
      currentMedicationIds.map(async (medicationId) => {
        try {
          await firstValueFrom(
            this.currentMedicationService.deleteCurrentMedication(
              medicationId.toString(),
            ),
          );
        } catch (error) {
          console.error('Unable to clean up draft medication', error);
        }
      }),
    );

    await Promise.all(
      stopMedicationIds.map(async (medicationId) => {
        try {
          await firstValueFrom(
            this.pastMedicationService.deletePastMedication(
              medicationId.toString(),
            ),
          );
        } catch (error) {
          console.error('Unable to clean up draft stop medication', error);
        }
      }),
    );

    await Promise.all(
      serviceRequestIds.map(async (serviceRequestId) => {
        try {
          await firstValueFrom(
            this.serviceRequestService.deleteServiceRequest(serviceRequestId),
          );
        } catch (error) {
          console.error('Unable to clean up draft service request', error);
        }
      }),
    );
  }

  private async cleanupDraftAppointment(appointmentId: number) {
    this.createdDraftAppointmentIds.delete(appointmentId);

    try {
      await firstValueFrom(
        this.appointmentService.deleteAppointment(appointmentId),
      );
    } catch (error) {
      console.error('Unable to clean up draft appointment', error);
    }
  }

  private async cleanupDraftMedication(row: LinkedMedicationRow) {
    if (row.actionTypeRaw === 'stop') {
      this.createdDraftStopMedicationIds.delete(row.medicationId);

      try {
        await firstValueFrom(
          this.pastMedicationService.deletePastMedication(
            row.medicationId.toString(),
          ),
        );
      } catch (error) {
        console.error('Unable to clean up draft stop medication', error);
      }

      return;
    }

    this.createdDraftCurrentMedicationIds.delete(row.medicationId);

    try {
      await firstValueFrom(
        this.currentMedicationService.deleteCurrentMedication(
          row.medicationId.toString(),
        ),
      );
    } catch (error) {
      console.error('Unable to clean up draft medication', error);
    }
  }

  private async cleanupDraftServiceRequest(serviceRequestId: number) {
    this.createdDraftServiceRequestIds.delete(serviceRequestId);

    try {
      await firstValueFrom(
        this.serviceRequestService.deleteServiceRequest(serviceRequestId),
      );
    } catch (error) {
      console.error('Unable to clean up draft service request', error);
    }
  }

  private hasDraftResourcesToCleanup(): boolean {
    return (
      this.createdDraftAppointmentIds.size > 0 ||
      this.createdDraftCurrentMedicationIds.size > 0 ||
      this.createdDraftStopMedicationIds.size > 0 ||
      this.createdDraftServiceRequestIds.size > 0
    );
  }

  private clearDraftResourceTracking() {
    this.createdDraftAppointmentIds.clear();
    this.createdDraftCurrentMedicationIds.clear();
    this.createdDraftStopMedicationIds.clear();
    this.createdDraftServiceRequestIds.clear();
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    const err = error as {
      error?: { message?: string };
      message?: string;
    };

    return err?.error?.message || err?.message || fallback;
  }
}
